import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import {
    ChevronLeft,
    Save,
    Download,
    Maximize2,
    Edit3,
    CheckCircle,
    AlertCircle,
    Palette,
    Loader2,
    Zap,
    Undo2,
    Eye,
    EyeOff,
    Plus,
    Trash2,
    BookOpen,
    Send,
    FileText,
    Pencil,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import ComicaPro from './ComicaPro';
import { AuthContext } from '../App';
import axios from 'axios';
import { useContext } from 'react';
import { generateLayouts } from '../utils/layoutGenerator';
import LayoutDesigner from './LayoutDesigner';
import { trackAIGeneration, trackEvent } from '../utils/analytics';
import { encodeGIF } from './editor/animation/gifEncoder';

const PanelProgressRow = ({ label, status, index }) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (status !== 'generating') {
            setProgress(status === 'done' ? 100 : 0);
            return;
        }

        setProgress(0);
        const startTime = Date.now();
        const duration = 50000; // ~50 seconds expected duration

        const timer = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const percentage = Math.min((elapsed / duration) * 95, 95); // hold at 95%
            setProgress(percentage);
        }, 100);

        return () => clearInterval(timer);
    }, [status]);

    return (
        <div className={`relative overflow-hidden flex flex-col gap-1.5 px-3 py-2.5 rounded-lg text-sm transition-all duration-300 ${
            status === 'done' ? 'bg-green-500/10 border border-green-500/20' :
            status === 'generating' ? 'bg-purple-500/10 border border-purple-500/20 shadow-md shadow-purple-500/5' :
            status === 'failed' ? 'bg-red-500/10 border border-red-500/20' :
            'bg-gray-800/40 border border-gray-700/30'
        }`}>
            {/* Smooth background progress loading bar for active panel */}
            {status === 'generating' && (
                <div 
                    className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-purple-500/10 to-indigo-500/20 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                />
            )}
            {status === 'done' && (
                <div className="absolute left-0 top-0 bottom-0 bg-green-500/5 w-full" />
            )}

            <div className="relative z-10 flex items-center justify-between">
                <span className="text-gray-200 font-semibold">{label}</span>
                <span className={`text-xs font-bold ${
                    status === 'done' ? 'text-green-400' :
                    status === 'generating' ? 'text-purple-400 animate-pulse' :
                    status === 'failed' ? 'text-red-400 font-bold' :
                    'text-gray-500'
                }`}>
                    {status === 'done' ? '✓ Done' : 
                     status === 'generating' ? `✨ Generating (${Math.round(progress)}%)...` : 
                     status === 'failed' ? '❌ Failed' : 
                     'Waiting'}
                </span>
            </div>

            {/* Tiny linear progress bar under text */}
            {status === 'generating' && (
                <div className="relative z-10 w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Comic Blueprint View
 * The central hub where users see their full page layout and select panels to edit.
 */
const ComicBlueprint = ({ renderMode = 'default', demoProject = null, onAuthRequired = null, forceNewProject = false }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectIdFromUrl = searchParams.get('project');
    const isReadOnly = location.state?.readOnly || searchParams.get('readOnly') === 'true' || searchParams.get('mode') === 'preview';

    // Helper to normalize project data (flatten canvas_data if present)
    const normalizeProject = (proj) => {
        if (!proj) return null;
        // If data is nested in canvas_data (from DB), flatten it
        if (proj.canvas_data) {
            return {
                ...proj,
                ...proj.canvas_data, // Overwrite root with canvas_data fields (layout, panels, etc)
            };
        }
        return proj;
    };

    // Project data passed from wizard OR fetched from ID
    const [project, setProject] = useState(normalizeProject(location.state?.project) || (renderMode === 'landing' ? demoProject : null));
    const [isLoading, setIsLoading] = useState(!location.state?.project && !!projectIdFromUrl && renderMode !== 'landing');
    const { token, API, user } = useContext(AuthContext);
    const [isSaving, setIsSaving] = useState(false);

    // 
    // MULTI-PAGE STATE
    // 
    // Each page: { layout, panelAssets, rawPanelAssets, borderColor }
    const [pages, setPages] = useState([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);

    // Convenience getters for current page
    const currentPage = pages[currentPageIndex] || {};
    const panelAssets = currentPage.panelAssets || {};
    const rawPanelAssets = currentPage.rawPanelAssets || {};
    const borderColor = currentPage.borderColor || '#ffffff';

    // Setter helpers that update the correct page
    const setPanelAssets = (updater) => {
        setPages(prev => prev.map((p, i) => i === currentPageIndex
            ? { ...p, panelAssets: typeof updater === 'function' ? updater(p.panelAssets || {}) : updater }
            : p
        ));
    };
    const setRawPanelAssets = (updater) => {
        setPages(prev => prev.map((p, i) => i === currentPageIndex
            ? { ...p, rawPanelAssets: typeof updater === 'function' ? updater(p.rawPanelAssets || {}) : updater }
            : p
        ));
    };
    const setBorderColor = (color) => {
        setPages(prev => prev.map((p, i) => i === currentPageIndex
            ? { ...p, borderColor: color }
            : p
        ));
    };

    // Currently active panel (for Focus Mode)
    const [activePanel, setActivePanel] = useState(null);
    // Track if the editor is processing a timelapse (blocks close button)
    const [editorProcessing, setEditorProcessing] = useState(false);

    // AI Auto-Generation State
    const [isAutoGenerating, setIsAutoGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ step: '', current: 0, total: 0, panelStatuses: [] });
    const autoGenTriggered = useRef(false);
    const autoGenFnRef = useRef(null); // Stores the auto-gen function for first-page choice

    // Match Style State
    const [isMatchMode, setIsMatchMode] = useState(false);
    const [isMatching, setIsMatching] = useState(false);
    const [matchStyleBackup, setMatchStyleBackup] = useState(null);

    // Raw/Styled Toggle
    const [showRawFrames, setShowRawFrames] = useState(false);

    // Panel border/gutter color
    const [showColorPicker, setShowColorPicker] = useState(false);
    const colorPickerRef = useRef(null);

    // Publish Comic Modal
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishMode, setPublishMode] = useState('new'); // 'new' or 'existing'
    const [publishData, setPublishData] = useState({ title: '', description: '', categories: [], tags: [] });
    const [myComics, setMyComics] = useState([]);
    const [selectedComicId, setSelectedComicId] = useState('');
    const [selectedEpisodeId, setSelectedEpisodeId] = useState('new'); // 'new' or episode uuid
    const [episodeTitle, setEpisodeTitle] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);

    // Add Page Modal — Enhanced Wizard
    const [showAddPageModal, setShowAddPageModal] = useState(false);
    const [newPagePanelCount, setNewPagePanelCount] = useState(4);
    const [addPageStep, setAddPageStep] = useState(1); // 1=layout, 2=generate-or-blank
    const [addPageOrientation, setAddPageOrientation] = useState('portrait');
    const [selectedNewLayout, setSelectedNewLayout] = useState(null);
    const [addPageStory, setAddPageStory] = useState('');
    const [addPageStyle, setAddPageStyle] = useState('');
    const [addPageChars, setAddPageChars] = useState('');
    const [addPageGenMethod, setAddPageGenMethod] = useState('image');
    const [addPageModel, setAddPageModel] = useState('caravaggio'); // 'caravaggio' or 'gemini'
    const [isPageGenerating, setIsPageGenerating] = useState(false);
    const [showCustomLayoutDesigner, setShowCustomLayoutDesigner] = useState(false);
    const [pageGenProgress, setPageGenProgress] = useState({ step: '', current: 0, total: 0, panelStatuses: [] });

    // Single Panel Regeneration Modal States
    const [showRegenModal, setShowRegenModal] = useState(false);
    const [regenPanel, setRegenPanel] = useState(null);
    const [regenPrompt, setRegenPrompt] = useState('');
    const [regenModel, setRegenModel] = useState('caravaggio'); // 'caravaggio' or 'gemini'
    const [isRegeneratingPanel, setIsRegeneratingPanel] = useState(false);
    const [regenProgress, setRegenProgress] = useState(0);
    const [regenRefImage, setRegenRefImage] = useState(null); // Per-panel character reference image
    const [regenFaceLock, setRegenFaceLock] = useState(true); // Per-panel face lock toggle
    const [regenPageIndex, setRegenPageIndex] = useState(0); // Captured page index for regen target
    const [costumeLockStrength, setCostumeLockStrength] = useState(0.85); // IP-Adapter scale (0.5-1.0)

    // Character Studio States
    const [characters, setCharacters] = useState([]); // [{id, name, description, imageUrl, imageBase64}]
    const [showCharacterModal, setShowCharacterModal] = useState(false);
    const [editCharName, setEditCharName] = useState('');
    const [editCharDesc, setEditCharDesc] = useState('');
    const [editCharImage, setEditCharImage] = useState(null); // base64 or url
    const [isGeneratingCharacter, setIsGeneratingCharacter] = useState(false);
    const [isDescribingChar, setIsDescribingChar] = useState(false); // Auto-describe loading
    const [faceLockEnabled, setFaceLockEnabled] = useState(true); // Default ON

    // Panel prompt memory — stores last used scene description per panel per page
    // Structure: { [pageIndex]: { [panelId]: "scene description" } }
    const [panelPrompts, setPanelPrompts] = useState({});

    const openRegenModal = (panel, currentAsset, pageIdx = currentPageIndex) => {
        setRegenPanel(panel);
        setRegenPageIndex(pageIdx); // Capture which page this panel belongs to
        // Pre-fill with stored prompt from last generation (if available)
        const storedPrompt = panelPrompts[pageIdx]?.[panel.id] || '';
        setRegenPrompt(storedPrompt);
        setRegenModel(addPageModel || 'caravaggio');
        // Pre-fill character reference from the character gallery anchor
        const anchor = characters.find(c => c.imageBase64 || c.imageUrl);
        setRegenRefImage(anchor?.imageBase64 || anchor?.imageUrl || null);
        setRegenFaceLock(faceLockEnabled);
        setRegenProgress(0);
        setIsRegeneratingPanel(false);
        setShowRegenModal(true);
    };

    const handleSinglePanelRegenerate = async () => {
        if (!regenPanel) return;
        if (!regenPrompt.trim()) {
            toast.error('Please enter a description for the panel');
            return;
        }

        setIsRegeneratingPanel(true);
        setRegenProgress(0);

        // Progress bar simulation interval
        const startTime = Date.now();
        const duration = 50000; // ~50s expected duration
        const timer = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const percentage = Math.min((elapsed / duration) * 95, 95);
            setRegenProgress(percentage);
        }, 100);

        try {
            const spec = {
                panel_id: String(regenPanel.id),
                scene_description: regenPrompt.trim(),
                width: Math.round(regenPanel.width),
                height: Math.round(regenPanel.height)
            };

            const response = await axios.post(`${API}/ai/batch-generate-panels`, {
                panels: [spec],
                style: addPageStyle || '',
                characters: characters.length > 0
                    ? characters.map(c => `CHARACTER "${c.name}": ${c.description}. MAINTAIN THIS EXACT APPEARANCE.`).join('\n')
                    : (addPageChars || ''),
                character_reference_image: (regenFaceLock && regenModel === 'caravaggio' && regenRefImage)
                    ? regenRefImage
                    : null,
                page_seed: null, // Allow fresh seed for individual regen (creative freedom)
                face_lock: regenFaceLock && regenModel === 'caravaggio' && !!regenRefImage,
                model_engine: regenModel,
                ip_adapter_scale: costumeLockStrength
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 300000  // 5 min for face-lock / cold-start
            });

            clearInterval(timer);

            if (response.data.success && response.data.panels && response.data.panels[0]) {
                const result = response.data.panels[0];
                if (result.success && result.image_url) {
                    setRegenProgress(100);
                    
                    // Replace the specific panel's asset immediately
                    // Use captured regenPageIndex (not currentPageIndex) to write to correct page
                    setPages(prev => prev.map((p, index) => {
                        if (index !== regenPageIndex) return p;
                        const updatedAssets = { ...p.panelAssets };
                        const updatedRawAssets = { ...p.rawPanelAssets };
                        
                        updatedAssets[regenPanel.id] = result.image_url;
                        if (result.raw_image_url) {
                            updatedRawAssets[regenPanel.id] = result.raw_image_url;
                        }
                        
                        return { ...p, panelAssets: updatedAssets, rawPanelAssets: updatedRawAssets };
                    }));

                    toast.success('Panel regenerated successfully!');
                    trackAIGeneration({ modelEngine: regenModel, success: true, panelCount: 1, source: 'regen' });
                    fetchCredits();

                    // Store the prompt used (for future regen pre-fill) — use captured page index
                    setPanelPrompts(prev => ({
                        ...prev,
                        [regenPageIndex]: {
                            ...(prev[regenPageIndex] || {}),
                            [regenPanel.id]: regenPrompt.trim()
                        }
                    }));
                    
                    // Close the modal cleanly after success
                    setTimeout(() => {
                        setShowRegenModal(false);
                        setRegenPanel(null);
                    }, 500);
                } else {
                    toast.error(result.error || 'Failed to regenerate panel');
                }
            } else {
                toast.error('Failed to regenerate panel');
            }
        } catch (err) {
            clearInterval(timer);
            console.error('Single panel regeneration error:', err);
            const msg = err.response?.data?.detail || err.message || 'Generation failed';
            toast.error(msg);
        } finally {
            setIsRegeneratingPanel(false);
        }
    };

    // First Page Choice (blank or generate)
    const [showFirstPageChoice, setShowFirstPageChoice] = useState(false);

    // Credits
    const [credits, setCredits] = useState(null);

    // Name Project Modal (for first save on landing)
    const [showNameProjectModal, setShowNameProjectModal] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    const titleInputRef = useRef(null);

    // ”€”€”€ Session persistence helpers (survive login redirect) ”€”€”€
    // Uses IndexedDB instead of sessionStorage to handle large panel assets (base64 images)
    const IDB_NAME = 'comicmenia_session';
    const IDB_STORE = 'guest_work';
    const IDB_KEY = 'current';

    const openSessionDB = () => {
        return new Promise((resolve, reject) => {
            const req = indexedDB.open(IDB_NAME, 1);
            req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    };

    const saveWorkToSession = async (pendingAction = null) => {
        try {
            const workData = {
                project: { title: project?.title, pageWidth: project?.pageWidth, pageHeight: project?.pageHeight, orientation: project?.orientation, panelCount: project?.panelCount },
                pages: pages.map((p, i) => ({
                    layout: p.layout,
                    panelAssets: i === currentPageIndex ? panelAssets : (p.panelAssets || {}),
                    rawPanelAssets: {},
                    borderColor: p.borderColor || '#ffffff'
                })),
                currentPageIndex,
                pendingAction,
                savedAt: Date.now()
            };
            const db = await openSessionDB();
            const tx = db.transaction(IDB_STORE, 'readwrite');
            tx.objectStore(IDB_STORE).put(workData, IDB_KEY);
            await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
            db.close();
            console.log('[Session] Saved guest work to IndexedDB', { pages: workData.pages.length, pendingAction });
        } catch (e) {
            console.warn('[Session] Failed to save work:', e.message);
        }
    };

    const restoreWorkFromSession = async () => {
        try {
            const db = await openSessionDB();
            const tx = db.transaction(IDB_STORE, 'readwrite');
            const store = tx.objectStore(IDB_STORE);
            const data = await new Promise((res, rej) => {
                const req = store.get(IDB_KEY);
                req.onsuccess = () => res(req.result);
                req.onerror = rej;
            });
            // Always delete after reading
            store.delete(IDB_KEY);
            await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
            db.close();
            if (!data) return null;
            // Only restore if saved within last 30 minutes
            if (Date.now() - data.savedAt > 30 * 60 * 1000) {
                return null;
            }
            return data;
        } catch (e) {
            console.warn('[Session] Failed to restore work:', e.message);
            return null;
        }
    };

    // ”€”€”€ Refs for unmount auto-save (closures go stale, refs don't) ”€”€”€
    const pagesRef = useRef(pages);
    const projectRef = useRef(project);
    const panelAssetsRef = useRef(panelAssets);
    const currentPageIndexRef = useRef(currentPageIndex);
    useEffect(() => { pagesRef.current = pages; }, [pages]);
    useEffect(() => { projectRef.current = project; }, [project]);
    useEffect(() => { panelAssetsRef.current = panelAssets; }, [panelAssets]);
    useEffect(() => { currentPageIndexRef.current = currentPageIndex; }, [currentPageIndex]);

    // ”€”€”€ Auto-save to IndexedDB on unmount (navigate away) ”€”€”€
    useEffect(() => {
        if (renderMode !== 'landing') return;
        return () => {
            const p = pagesRef.current;
            if (!p || p.length === 0) return;
            try {
                const proj = projectRef.current;
                const workData = {
                    project: { title: proj?.title, pageWidth: proj?.pageWidth, pageHeight: proj?.pageHeight, orientation: proj?.orientation, panelCount: proj?.panelCount, id: proj?.id },
                    pages: p.map((pg, i) => ({
                        layout: pg.layout,
                        pageWidth: pg.pageWidth || proj?.pageWidth || 1200,
                        pageHeight: pg.pageHeight || proj?.pageHeight || 1600,
                        orientation: pg.orientation || proj?.orientation || 'portrait',
                        panelAssets: i === currentPageIndexRef.current ? panelAssetsRef.current : (pg.panelAssets || {}),
                        rawPanelAssets: {},
                        borderColor: pg.borderColor || '#ffffff'
                    })),
                    currentPageIndex: currentPageIndexRef.current,
                    pendingAction: null,
                    savedAt: Date.now()
                };
                // Fire-and-forget IndexedDB write (can't await in cleanup)
                openSessionDB().then(db => {
                    const tx = db.transaction(IDB_STORE, 'readwrite');
                    tx.objectStore(IDB_STORE).put(workData, IDB_KEY);
                    tx.oncomplete = () => { db.close(); console.log('[Session] Auto-saved on unmount', { pages: workData.pages.length }); };
                    tx.onerror = () => { db.close(); console.warn('[Session] Auto-save on unmount failed'); };
                }).catch(e => console.warn('[Session] Auto-save on unmount failed:', e.message));
            } catch (e) {
                console.warn('[Session] Auto-save on unmount failed:', e.message);
            }
        };
    }, [renderMode]);

    const fetchCredits = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ai/credits`, { headers: { Authorization: `Bearer ${token}` } });
            setCredits(res.data.credits);
        } catch (e) {
            console.error('Failed to fetch credits:', e);
        }
    }, [API, token]);

    useEffect(() => {
        if (token) fetchCredits();
    }, [token, fetchCredits]);

    // Close color picker on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
                setShowColorPicker(false);
            }
        };
        if (showColorPicker) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showColorPicker]);

    // 
    // HYDRATION: Load project and initialize pages
    // 
    useEffect(() => {
        if (!project) return;

        // If project has an ID but no usable canvas data, fetch the full project from API
        // This happens when coming from Dashboard Edit or Landing Page Read-Only preview
        const hasFullData = project.canvas_data?.pages || project.canvas_data?.version || project.layout;
        // Skip fetch for fallback/showcase IDs (e.g. 'fb-1') that don't exist in the database
        const isFallbackId = project.id && project.id.startsWith('fb-');
        if (project.id && !hasFullData && !isFallbackId) {
            console.log('[Hydration] Project has ID but no canvas data, fetching full project:', project.id, { isReadOnly });
            const fetchFullProject = async () => {
                try {
                    setIsLoading(true);
                    const endpoint = (isReadOnly || !token) ? `${API}/projects/public/${project.id}` : `${API}/projects/${project.id}`;
                    const headers = token ? { Authorization: `Bearer ${token}` } : {};
                    const response = await axios.get(endpoint, { headers });
                    if (response.data) {
                        const rawProject = response.data.project || response.data;
                        setProject(normalizeProject(rawProject));
                    }
                } catch (e) {
                    console.error("Failed to fetch full project:", e);
                    toast.error("Failed to load project data");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchFullProject();
            return;
        }

        // Multi-page format: canvas_data.pages is an array
        if (project.canvas_data?.pages) {
            const loadedPages = project.canvas_data.pages.map(pageData => {
                const loadedAssets = {};
                const panels = pageData.panels || {};
                Object.entries(panels).forEach(([pid, pData]) => {
                    loadedAssets[pid] = {
                        layers: pData.layers,
                        preview: pData.preview || null,
                        ...(pData.animation ? { animation: pData.animation } : {})
                    };
                });
                return {
                    layout: pageData.layout,
                    pageWidth: pageData.pageWidth || project.pageWidth || 1200,
                    pageHeight: pageData.pageHeight || project.pageHeight || 1600,
                    orientation: pageData.orientation || project.orientation || 'portrait',
                    panelAssets: loadedAssets,
                    rawPanelAssets: {},
                    borderColor: pageData.borderColor || '#ffffff'
                };
            });
            if (loadedPages.length > 0) {
                setPages(loadedPages);
                return;
            }
        }

        // Old single-page format: canvas_data.version === 'v2_blueprint'
        if (project.canvas_data?.version === 'v2_blueprint') {
            const loadedAssets = {};
            const panels = project.canvas_data.panels || {};
            Object.entries(panels).forEach(([pid, pData]) => {
                loadedAssets[pid] = {
                    layers: pData.layers,
                    preview: pData.preview || null
                };
            });
            setPages([{
                layout: project.canvas_data.layout || project.layout,
                pageWidth: project.pageWidth || 1200,
                pageHeight: project.pageHeight || 1600,
                orientation: project.orientation || 'portrait',
                panelAssets: loadedAssets,
                rawPanelAssets: {},
                borderColor: '#ffffff'
            }]);
            return;
        }

        // Brand new project — initial page from wizard layout
        if (project.layout && pages.length === 0) {
            setPages([{
                layout: project.layout,
                pageWidth: project.pageWidth || 1200,
                pageHeight: project.pageHeight || 1600,
                orientation: project.orientation || 'portrait',
                panelAssets: {},
                rawPanelAssets: {},
                borderColor: '#ffffff'
            }]);
        }
    }, [project]);

    // ”€”€”€ Restore guest work from IndexedDB after login ”€”€”€
    useEffect(() => {
        if (renderMode !== 'landing') return;
        // If forceNewProject is set, clear any saved session and skip restore
        if (forceNewProject) {
            openSessionDB().then(db => {
                const tx = db.transaction(IDB_STORE, 'readwrite');
                tx.objectStore(IDB_STORE).delete(IDB_KEY);
                tx.oncomplete = () => db.close();
                tx.onerror = () => db.close();
            }).catch(() => {});
            return;
        }
        const doRestore = async () => {
            const saved = await restoreWorkFromSession();
            if (!saved || !saved.pages || saved.pages.length === 0) return;

            console.log('[Session] Restoring guest work:', saved.pages.length, 'pages, pending:', saved.pendingAction);
            toast.success('Welcome back! Your work has been restored.');

            // Restore project title and id
            if (saved.project) {
                setProject(p => ({ ...p, title: saved.project.title || p.title, id: saved.project.id || p.id }));
            }
            // Restore pages
            setPages(saved.pages);
            setCurrentPageIndex(saved.currentPageIndex || 0);

            // Handle pending action after a short delay (let state settle)
            if (saved.pendingAction && token) {
                setTimeout(() => {
                    if (saved.pendingAction === 'save your draft' || saved.pendingAction === 'save') {
                        setShowNameProjectModal(true);
                    } else if (saved.pendingAction === 'add more pages') {
                        openAddPageModal();
                    }
                }, 500);
            }
        };
        doRestore();
    }, [renderMode, token]);

    // AUTO-GENERATION: If project has a story, show choice dialog (blank or generate)
    useEffect(() => {
        console.log('[AutoGen] Checking conditions...', {
            hasProject: !!project,
            story: project?.story,
            hasLayout: !!pages[0]?.layout,
            panelCount: pages[0]?.layout?.panels?.length,
            autoGenTriggered: autoGenTriggered.current,
            panelAssetsCount: Object.keys(panelAssets).length,
            hasToken: !!token,
            generationMethod: project?.generationMethod
        });

        if (!project || !project.story || !project.story.trim()) {
            console.log('[AutoGen] SKIP: No project or no story');
            return;
        }
        if (!pages[0]?.layout || !pages[0].layout.panels || pages[0].layout.panels.length === 0) {
            console.log('[AutoGen] SKIP: No layout or no panels');
            return;
        }
        if (autoGenTriggered.current) {
            console.log('[AutoGen] SKIP: Already triggered');
            return;
        }
        // Don't auto-gen if panels already have content
        if (Object.keys(panelAssets).length > 0) {
            console.log('[AutoGen] SKIP: Panels already have content');
            return;
        }
        if (!token) {
            console.log('[AutoGen] SKIP: No auth token');
            return;
        }

        // Show choice dialog instead of auto-generating
        const isVideoMode = project.generationMethod === 'video';
        console.log(`[AutoGen] Conditions met (${isVideoMode ? 'VIDEO' : 'IMAGE'} mode) — showing blank/generate choice dialog`);
        autoGenTriggered.current = true;
        setShowFirstPageChoice(true);

        const runAutoGeneration = async () => {
            setIsAutoGenerating(true);
            const panels = pages[0].layout.panels;

            try {
                // Step 1: Break story into panel scenes (used for BOTH modes)
                setGenerationProgress({
                    step: isVideoMode ? 'Preparing story for video...' : 'Breaking story into scenes...',
                    current: 0,
                    total: panels.length,
                    panelStatuses: panels.map(p => ({ id: p.id, status: 'waiting' }))
                });

                const storyRes = await axios.post(`${API}/ai/story-to-panels`, {
                    story: project.story,
                    panel_count: panels.length,
                    style: project.artStyle || null,
                    characters: project.characterDescriptions || null
                }, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 120000 // 2 min timeout for story decomposition
                });

                if (!storyRes.data.success || !storyRes.data.panels) {
                    throw new Error('Failed to decompose story');
                }

                const sceneDescriptions = storyRes.data.panels;

                // Step 2: Build panel specs with dimensions + scenes
                const panelSpecs = panels.map((panel, idx) => {
                    const scene = sceneDescriptions[idx];
                    return {
                        panel_id: String(panel.id),
                        scene_description: scene ? `${scene.scene} Camera: ${scene.camera}. Mood: ${scene.mood}.` : `Panel ${idx + 1}`,
                        width: Math.round(panel.width),
                        height: Math.round(panel.height)
                    };
                });

                // Step 3: Generate panels using selected method
                let batchRes;

                if (isVideoMode) {
                    //  VIDEO MODE: Generate video  extract frames 
                    setGenerationProgress(prev => ({
                        ...prev,
                        step: '🎬 Generating video & stylizing panels (~3-5 min)...',
                        panelStatuses: panels.map(p => ({ id: p.id, status: 'generating' }))
                    }));

                    batchRes = await axios.post(`${API}/ai/video-generate-panels`, {
                        panels: panelSpecs,
                        story: project.story,
                        style: project.artStyle || '',
                        characters: project.characterDescriptions || '',
                        frame_strategy: 'scene_change'
                    }, {
                        headers: { Authorization: `Bearer ${token}` },
                        timeout: 600000 // 10 min timeout for video generation
                    });

                } else {
                    //  IMAGE MODE: Generate one image per panel 
                    setGenerationProgress(prev => ({
                        ...prev,
                        step: 'Generating panel images...',
                        panelStatuses: panels.map(p => ({ id: p.id, status: 'generating' }))
                    }));

                    batchRes = await axios.post(`${API}/ai/batch-generate-panels`, {
                        panels: panelSpecs,
                        style: project.artStyle || '',
                        characters: project.characterDescriptions || ''
                    }, {
                        headers: { Authorization: `Bearer ${token}` },
                        timeout: 300000 // 5 min timeout for batch
                    });
                }

                if (batchRes.data.success && batchRes.data.panels) {
                    // Populate panelAssets with generated images
                    const newAssets = {};
                    const updatedStatuses = [];

                    const newRawAssets = {};
                    batchRes.data.panels.forEach(result => {
                        if (result.success && result.image_url) {
                            newAssets[result.panel_id] = result.image_url;
                            updatedStatuses.push({ id: result.panel_id, status: 'done' });
                            // Store raw WAN frame URL if available
                            if (result.raw_image_url) {
                                newRawAssets[result.panel_id] = result.raw_image_url;
                            }
                        } else {
                            updatedStatuses.push({ id: result.panel_id, status: 'failed' });
                        }
                    });

                    setPanelAssets(prev => ({ ...prev, ...newAssets }));
                    if (Object.keys(newRawAssets).length > 0) {
                        setRawPanelAssets(prev => ({ ...prev, ...newRawAssets }));
                    }
                    setGenerationProgress(prev => ({ ...prev, step: 'Complete!', panelStatuses: updatedStatuses }));

                    const successCount = batchRes.data.panels.filter(p => p.success).length;
                    const modeLabel = isVideoMode ? '🎬 Video' : '📸 Image';
                    toast.success(`${modeLabel}: Generated ${successCount}/${panels.length} panels! (${batchRes.data.credits_used} credits used)`);
                    trackAIGeneration({ modelEngine: 'gemini', success: true, panelCount: successCount, source: 'auto_gen' });
                    fetchCredits(); // refresh credit counter
                }

            } catch (error) {
                console.error('Auto-generation error:', error);
                const msg = error.response?.data?.detail || error.message || 'Auto-generation failed';
                toast.error(msg);
            } finally {
                // Delay hiding overlay so user sees completion state
                setTimeout(() => setIsAutoGenerating(false), 1500);
            }
        };
        // Store the function so the choice dialog can trigger it
        autoGenFnRef.current = runAutoGeneration;
        // Don't auto-call — user will choose via the showFirstPageChoice dialog
    }, [project, token, API, panelAssets]);


    // Fetch project if ID is in URL but not in state (Deep Link / Refresh)
    useEffect(() => {
        if (!project && projectIdFromUrl && token) {
            const fetchProject = async () => {
                try {
                    setIsLoading(true);
                    const response = await axios.get(`${API}/projects/${projectIdFromUrl}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.data) {
                        // Normalize structure in case it's deeply nested
                        const rawProject = response.data.project || response.data;
                        setProject(normalizeProject(rawProject));
                        // Hydration of panels happens in the other useEffect automatically once 'project' is set
                    }
                } catch (e) {
                    console.error("Failed to fetch project:", e);
                    toast.error("Failed to load project draft");
                    navigate('/dashboard');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchProject();
        } else if (!project && !projectIdFromUrl && renderMode !== 'landing') {
            // No ID, no state -> Redirect
            toast.error("No project data found. Returning to create.");
            navigate('/create-comic-flow');
        }
    }, [project, projectIdFromUrl, token, API, navigate]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-white">
                <div className="animate-spin mb-4 h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full" />
                <p>Loading Blueprint...</p>
            </div>
        );
    }

    if (!project) return null;

    // Calculate scaling to fit viewport
    // We want the page to be visible with some padding
    const maxH = Math.min(window.innerHeight - 150, 900);
    const maxW = Math.min(window.innerWidth - 100, 1200); // 100px width padding
    const currentPageWidth = currentPage.pageWidth || project.pageWidth || 1200;
    const currentPageHeight = currentPage.pageHeight || project.pageHeight || 1600;

    // Handler for panel click
    const handlePanelClick = (panel) => {
        setActivePanel(panel);
    };

    // Helper: get what to DISPLAY (preview = edited version, fallback to fullImage or raw string)
    const getPanelPreview = (asset) => {
        if (!asset) return null;
        if (typeof asset === 'string') return asset;
        return asset.preview || asset.fullImage || null;
    };

    // Helper: get the original full-res image for RE-EDITING in the editor
    const getEditableImage = (asset) => {
        if (!asset) return null;
        if (typeof asset === 'string') return asset;
        return asset.fullImage || asset.preview || null;
    };

    // Save handler from Editor
    const handleEditorSave = (data) => {
        // data is { preview, fullImage, layers } or legacy string
        // Always preserve the fullImage and layers from the incoming data or the existing asset
        const existing = panelAssets[activePanel.id];
        const existingFullImage = typeof existing === 'string' ? existing : existing?.fullImage;
        const existingLayers = typeof existing === 'object' ? existing?.layers : null;

        let mergedData = data;
        if (typeof data === 'object') {
            mergedData = {
                ...data,
                fullImage: data.fullImage || existingFullImage || null,
                layers: data.layers || existingLayers || null
            };
        } else if (typeof data === 'string' && existingFullImage) {
            // Legacy string save €” wrap it but keep the fullImage + layers
            mergedData = { preview: data, fullImage: existingFullImage, layers: existingLayers };
        }

        setPanelAssets(prev => ({
            ...prev,
            [activePanel.id]: mergedData
        }));
        setActivePanel(null); // Close editor
        setMatchStyleBackup(null); // Clear undo €” user manually edited a panel
        toast.success(`Panel ${activePanel.id} updated!`);
    };

    // Helper to upload base64 image
    const uploadBase64Image = async (base64Data) => {
        try {
            // Convert base64 to blob
            const response = await fetch(base64Data);
            const blob = await response.blob();
            const file = new File([blob], "layer.png", { type: "image/png" });

            const formData = new FormData();
            formData.append('file', file);

            const uploadRes = await axios.post(`${API}/upload/image`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            return uploadRes.data.url;
        } catch (e) {
            console.error("Upload failed", e);
            throw e;
        }
    };

    //  Match Style: User selected a reference panel 
    const handleMatchStyleSelect = async (referencePanel) => {
        setIsMatchMode(false);
        setIsMatching(true);
        const toastId = toast.loading(`Matching all panels to ${referencePanel.id.toUpperCase()} style...`);

        try {
            const referenceImage = getPanelPreview(panelAssets[referencePanel.id]);

            if (!referenceImage) {
                toast.error('Reference panel has no image', { id: toastId });
                setIsMatching(false);
                return;
            }

            // Collect all OTHER panels that have images
            const panelsToMatch = [];
            const layoutPanels = currentPage.layout?.panels || project?.layout?.panels;
            if (layoutPanels) {
                for (const panel of layoutPanels) {
                    if (panel.id === referencePanel.id) continue; // skip reference
                    const asset = panelAssets[panel.id];
                    const img = getPanelPreview(asset);
                    if (img) {
                        panelsToMatch.push({
                            panel_id: panel.id,
                            image: img,
                            width: panel.width,
                            height: panel.height
                        });
                    }
                }
            }

            if (panelsToMatch.length === 0) {
                toast.info('No other panels with images to match.', { id: toastId });
                setIsMatching(false);
                return;
            }

            console.log(`[MatchStyle] Reference: ${referencePanel.id}, Panels to match: ${panelsToMatch.length}`);
            console.log(`[MatchStyle] Reference image type: ${typeof referenceImage}, starts with: ${referenceImage?.substring(0, 30)}`);
            console.log(`[MatchStyle] Panel images:`, panelsToMatch.map(p => ({ id: p.panel_id, imgType: typeof p.image, starts: p.image?.substring(0, 30) })));

            const response = await axios.post(`${API}/ai/match-panel-styles`, {
                reference_image: referenceImage,
                panels: panelsToMatch
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000, // 2 min timeout for multiple panel re-generation
                maxContentLength: Infinity,
                maxBodyLength: Infinity
            });

            if (response.data?.success && response.data.panels) {
                const updatedAssets = { ...panelAssets };
                let matchedCount = 0;

                for (const result of response.data.panels) {
                    if (result.success && result.image_url) {
                        const existing = updatedAssets[result.panel_id];
                        if (typeof existing === 'object' && existing !== null) {
                            // Update image fields and clear stale layers
                            // (layers had old imageData that would fail to load)
                            // The editor will load the new styled image fresh as initialImage
                            const { layers, ...rest } = existing;
                            updatedAssets[result.panel_id] = {
                                ...rest,
                                preview: result.image_url,
                                fullImage: result.image_url
                            };
                        } else {
                            updatedAssets[result.panel_id] = result.image_url;
                        }
                        matchedCount++;
                    }
                }

                setMatchStyleBackup({ ...panelAssets }); // Save pre-match snapshot for undo
                setPanelAssets(updatedAssets);
                toast.success(`${matchedCount} panels matched! (1 credit used)`, { id: toastId });
                fetchCredits(); // refresh credit counter
                console.log(`[MatchStyle] Done: ${matchedCount} panels restyled, style: ${response.data.style_description}`);
            } else {
                toast.error('Style matching failed -- no panels updated.', { id: toastId });
            }
        } catch (err) {
            console.error('[MatchStyle] Error:', err);
            console.error('[MatchStyle] Response data:', JSON.stringify(err?.response?.data));
            let msg = 'Unknown error';
            const detail = err?.response?.data?.detail;
            if (typeof detail === 'string') {
                msg = detail;
            } else if (Array.isArray(detail)) {
                // FastAPI 422 validation error format
                msg = detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join('; ');
            } else if (err.message) {
                msg = err.message;
            }
            toast.error(`Match Style failed: ${msg}`, { id: toastId });
        } finally {
            setIsMatching(false);
        }
    };

    // Save Draft Functionality
    const handleSaveDraft = async () => {
        if (!project || !project.title) {
            toast.error("Invalid project data");
            return;
        }

        setIsSaving(true);
        const toastId = toast.loading("Saving draft... (Uploading assets)");
        try {
            //  Helper: process one page's panels (upload base64, format to {preview, layers}) 
            const processPagePanels = async (pageAssets) => {
                const processed = {};
                for (const [panelId, assetData] of Object.entries(pageAssets || {})) {
                    let layers = [];
                    let preview = null;

                    if (typeof assetData === 'string') {
                        preview = assetData;
                        layers = [{
                            id: 1, name: 'Background', visible: true, opacity: 1,
                            imageData: assetData
                        }];
                    } else {
                        preview = assetData.preview || assetData.fullImage;
                        if (assetData.layers && assetData.layers.length > 0) {
                            layers = [...assetData.layers];
                        } else {
                            layers = [{
                                id: 1, name: 'Background', visible: true, opacity: 1,
                                imageData: preview
                            }];
                        }
                    }

                    // Upload base64 layer images to Cloudinary
                    for (let i = 0; i < layers.length; i++) {
                        if (layers[i].imageData && layers[i].imageData.startsWith('data:')) {
                            const url = await uploadBase64Image(layers[i].imageData);
                            layers[i].imageData = url;
                        }
                    }

                    // Upload preview if base64
                    if (preview && preview.startsWith('data:')) {
                        preview = await uploadBase64Image(preview);
                    }

                    processed[panelId] = { preview, layers };
                    // Keep AnimCanvas data (already uploaded URLs) so animated panels survive save/reload
                    if (assetData && typeof assetData === 'object' && assetData.animation) {
                        processed[panelId].animation = assetData.animation;
                    }
                }
                return processed;
            };

            //  Process ALL pages (not just current) 
            const pagesPayload = [];
            for (let pi = 0; pi < pages.length; pi++) {
                const page = pages[pi];
                const pageAssets = pi === currentPageIndex ? panelAssets : (page.panelAssets || {});
                const processedPanels = await processPagePanels(pageAssets);
                pagesPayload.push({
                    layout: page.layout,
                    pageWidth: page.pageWidth || project.pageWidth || 1200,
                    pageHeight: page.pageHeight || project.pageHeight || 1600,
                    orientation: page.orientation || project.orientation || 'portrait',
                    panels: processedPanels,
                    borderColor: page.borderColor || '#ffffff'
                });
            }

            // Calculate thumbnail from first page's first panel
            let thumbnail = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
            if (pagesPayload.length > 0) {
                const firstPagePanels = pagesPayload[0].panels;
                const firstPanelId = Object.keys(firstPagePanels)[0];
                if (firstPanelId && firstPagePanels[firstPanelId]?.preview) {
                    thumbnail = firstPagePanels[firstPanelId].preview;
                }
            }

            const payload = {
                title: project.title,
                thumbnail: thumbnail,
                width: currentPageWidth,
                height: currentPageHeight,
                canvas_data: {
                    version: "v3_multipage",
                    pages: pagesPayload,
                    pageWidth: currentPageWidth,
                    pageHeight: currentPageHeight,
                    orientation: currentPage.orientation || project.orientation,
                    panelCount: project.panelCount
                },
                is_public: false
            };

            // Calculate overall thumbnail (rough approximation: composite of panels)
            // Ideally we'd capture the main container, but that's hard from here.
            // We can update the 'thumbnail' field if we had a way to snapshot the DOM.
            // For now, let's leave thumbnail updating to the specific endpoint logic or reuse first panel.

            // Call Save API
            // If project has ID, we update. If not, we create new?
            // The /projects/save endpoint usually creates new. /projects/{id} updates.
            // Let's use /projects/save for now which handles both usually or creates new.
            // Actually server.py /projects/save creates NEW.
            // We need update logic if it exists.

            let response;
            const isOwner = project.user_id && user && project.user_id === user.id;
            if (project.id && !isReadOnly && isOwner) {
                // Update existing owned project
                response = await axios.put(`${API}/projects/${project.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                // Save as a new project copy for the current user
                response = await axios.post(`${API}/projects/save`, {
                    ...payload,
                    title: project.title ? (isOwner ? project.title : `${project.title} (Copy)`) : 'Untitled Comic',
                    description: `Blueprint Copy - Saved from ${project.author_name || 'Public Gallery'}`
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Update local project with new ID
                const newId = response.data.project_id || response.data.id || response.data._id;
                if (newId) {
                    setProject(p => ({ ...p, id: newId, is_public: false }));
                }
            }

            toast.success("Draft saved successfully!", { id: toastId });

        } catch (e) {
            console.error("Save Draft Error", e);
            toast.error("Failed to save draft", { id: toastId });
        } finally {
            setIsSaving(false);
        }
    };

    // Final Assembly & Export
    const handleExport = () => {
        console.log("Starting Export...");
        const canvas = document.createElement('canvas');
        canvas.width = currentPageWidth;
        canvas.height = currentPageHeight;
        const ctx = canvas.getContext('2d');

        console.log(`Canvas Created: ${canvas.width}x${canvas.height}`);

        // Draw Background with user-selected border color
        ctx.fillStyle = borderColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const layout = currentPage.layout;
        if (!layout || !layout.panels) {
            toast.error("No layout on current page!");
            return;
        }
        const panelsToDraw = layout.panels.filter(p => panelAssets[p.id]);
        console.log(`Found ${panelsToDraw.length} panels with assets out of ${layout.panels.length} total panels.`);
        console.log("Panel Assets Keys:", Object.keys(panelAssets));

        if (panelsToDraw.length === 0) {
            console.warn("No panels have content to export!");
            toast.error("No panels have content to export!");
            return;
        }

        const processExport = () => {
            console.log("All images loaded/processed. Triggering download.");
            try {
                const link = document.createElement('a');
                link.download = `${project.title.replace(/\s+/g, '_')}_comic_page.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                toast.success("Comic page exported successfully!");
            } catch (e) {
                console.error('Export toDataURL failed (canvas tainted?):', e);
                toast.error('Export failed -- try re-clicking the page and exporting again.');
            }
        };

        // Helper: load image with CORS, retry with cache-bust on failure
        const loadImageWithRetry = (src) => {
            return new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => resolve(img);
                img.onerror = () => {
                    // Retry with cache-busting query param to bypass stale CORS cache
                    console.warn(`CORS cache miss, retrying with cache-bust: ${src.substring(0, 50)}...`);
                    const img2 = new Image();
                    img2.crossOrigin = "Anonymous";
                    img2.onload = () => resolve(img2);
                    img2.onerror = () => {
                        console.error(`Image load failed even after retry: ${src.substring(0, 50)}`);
                        resolve(null); // give up on this panel
                    };
                    const bustChar = src.includes('?') ? '&' : '?';
                    img2.src = src + bustChar + '_cb=' + Date.now();
                };
                img.src = src;
            });
        };

        // Load and draw each panel image
        const loadPromises = layout.panels.map(panel => {
            return new Promise(async (resolve) => {
                const asset = panelAssets[panel.id];
                let src = null;

                if (typeof asset === 'string') {
                    src = asset;
                } else if (asset && typeof asset === 'object') {
                    src = asset.preview || asset.fullImage;
                }

                if (src) {
                    console.log(`Loading image for panel ${panel.id}: ${(typeof src === 'string') ? src.substring(0, 50) : 'blob'}...`);
                    const img = await loadImageWithRetry(src);

                    if (img) {
                        console.log(`Image loaded for ${panel.id}`);
                        ctx.save();

                        // Clip to panel bounds (custom shape or rectangle)
                        if (panel.points && panel.points.length > 0) {
                            ctx.beginPath();
                            panel.points.forEach((pt, idx) => {
                                const px = (pt.x / 100) * panel.width + panel.x;
                                const py = (pt.y / 100) * panel.height + panel.y;
                                if (idx === 0) ctx.moveTo(px, py);
                                else ctx.lineTo(px, py);
                            });
                            ctx.closePath();
                            ctx.clip();
                        } else {
                            ctx.beginPath();
                            ctx.rect(panel.x, panel.y, panel.width, panel.height);
                            ctx.clip();
                        }

                        // Draw Image with cover-fit (preserve aspect ratio, fill panel)
                        const iw = img.naturalWidth;
                        const ih = img.naturalHeight;
                        const pw = panel.width;
                        const ph = panel.height;
                        const coverScale = Math.max(pw / iw, ph / ih);
                        const sw = iw * coverScale;
                        const sh = ih * coverScale;
                        const sx = panel.x + (pw - sw) / 2;
                        const sy = panel.y + (ph - sh) / 2;
                        ctx.drawImage(img, sx, sy, sw, sh);

                        // Draw Border (Stroke along the path)
                        if (panel.points && panel.points.length > 0) {
                            ctx.strokeStyle = '#000000';
                            ctx.lineWidth = 4;
                            ctx.stroke();
                        } else {
                            ctx.strokeStyle = '#000000';
                            ctx.lineWidth = 4;
                            ctx.strokeRect(panel.x, panel.y, panel.width, panel.height);
                        }

                        ctx.restore();
                    }
                    resolve();
                } else {
                    resolve();
                }
            });
        });

        Promise.all(loadPromises).then(processExport);
    };

    // 
    // EXPORT PAGE AS DATA URL (for publishing)
    // 
    // ─────────────────────────────────────────────────────────
    // ANIMATED PAGE EXPORT (AnimCanvas panels → animated GIF page)
    // Only used when a page contains at least one AnimCanvas panel;
    // static pages keep using the original PNG export below.
    // ─────────────────────────────────────────────────────────
    const pageHasAnimation = (page) => Object.values((page && page.panelAssets) || {}).some(
        a => a && typeof a === 'object' && a.animation && a.animation.sprite && a.animation.sprite.url
    );

    const exportAnimatedPageAsDataURL = async (pageIdx) => {
        const page = pages[pageIdx];
        if (!page || !page.layout) return null;
        const pageWidth = page.pageWidth || project.pageWidth || 1200;
        const pageHeight = page.pageHeight || project.pageHeight || 1600;
        const assets = page.panelAssets || {};

        const loadImg = (src) => new Promise((res) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => res(img);
            img.onerror = () => {
                const img2 = new Image();
                img2.crossOrigin = 'Anonymous';
                img2.onload = () => res(img2);
                img2.onerror = () => res(null);
                img2.src = src + (src.includes('?') ? '&' : '?') + '_cb=' + Date.now();
            };
            img.src = src;
        });
        const tracePanel = (ctx, panel) => {
            ctx.beginPath();
            if (panel.points && panel.points.length > 0) {
                panel.points.forEach((pt, idx) => {
                    const px = (pt.x / 100) * panel.width + panel.x;
                    const py = (pt.y / 100) * panel.height + panel.y;
                    if (idx === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
                });
                ctx.closePath();
            } else {
                ctx.rect(panel.x, panel.y, panel.width, panel.height);
            }
        };
        const drawInPanel = (ctx, panel, source, sx, sy, sw, sh) => {
            ctx.save();
            tracePanel(ctx, panel);
            ctx.clip();
            const cover = Math.max(panel.width / sw, panel.height / sh);
            const dw = sw * cover, dh = sh * cover;
            ctx.drawImage(source, sx, sy, sw, sh, panel.x + (panel.width - dw) / 2, panel.y + (panel.height - dh) / 2, dw, dh);
            tracePanel(ctx, panel);
            ctx.strokeStyle = '#000000'; ctx.lineWidth = 4; ctx.stroke();
            ctx.restore();
        };

        // Load everything once, in panel order (keeps overlap order)
        const items = [];
        for (const panel of page.layout.panels) {
            const asset = assets[panel.id];
            const anim = asset && typeof asset === 'object' && asset.animation && asset.animation.sprite ? asset.animation : null;
            if (anim) {
                const sheet = await loadImg(anim.sprite.url);
                const delays = Array.isArray(anim.delays) && anim.delays.length ? anim.delays.map(d => Math.max(20, Number(d) || 83)) : null;
                if (sheet && delays) {
                    items.push({ panel, anim, sheet, delays, duration: delays.reduce((s, d) => s + d, 0) });
                    continue;
                }
            }
            let src = null;
            if (typeof asset === 'string') src = asset;
            else if (asset && typeof asset === 'object') src = (anim && anim.poster) || asset.preview || asset.fullImage;
            if (src) {
                const img = await loadImg(src);
                if (img) items.push({ panel, img });
            }
        }
        const animated = items.filter(it => it.anim);
        if (!animated.length) return null;

        // Merge every animation's frame-change times into one GIF timeline
        const total = Math.min(Math.max(...animated.map(a => a.duration)), 30000);
        const times = new Set([0]);
        for (const a of animated) {
            let t = 0;
            while (t < total) {
                for (const d of a.delays) { t += d; if (t >= total) break; times.add(Math.round(t / 10) * 10); }
                if (a.anim.loop === false) break;
            }
        }
        let T = [...times].filter(t => t < total).sort((x, y) => x - y);
        if (T.length > 300) { const step = total / 300; T = Array.from({ length: 300 }, (_, i) => Math.round(i * step)); }

        const frameAt = (a, t) => {
            const tt = a.anim.loop === false ? Math.min(t, a.duration - 1) : t % a.duration;
            let acc = 0;
            for (let i = 0; i < a.delays.length; i++) { acc += a.delays[i]; if (tt < acc) return i; }
            return a.delays.length - 1;
        };

        const renderGif = async (maxWidth) => {
            const scale = Math.min(1, maxWidth / pageWidth);
            const W = Math.max(1, Math.round(pageWidth * scale)), H = Math.max(1, Math.round(pageHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = W; canvas.height = H;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            return encodeGIF({
                width: W, height: H, frameCount: T.length,
                getFrame: (i) => {
                    ctx.setTransform(scale, 0, 0, scale, 0, 0);
                    ctx.fillStyle = page.borderColor || '#ffffff';
                    ctx.fillRect(0, 0, pageWidth, pageHeight);
                    for (const it of items) {
                        if (it.anim) {
                            const f = Math.min(frameAt(it, T[i]), (it.anim.frameCount || it.delays.length) - 1);
                            const { cols, frameW, frameH } = it.anim.sprite;
                            drawInPanel(ctx, it.panel, it.sheet, (f % cols) * frameW, Math.floor(f / cols) * frameH, frameW, frameH);
                        } else {
                            drawInPanel(ctx, it.panel, it.img, 0, 0, it.img.naturalWidth, it.img.naturalHeight);
                        }
                    }
                    ctx.setTransform(1, 0, 0, 1, 0, 0);
                    return ctx.getImageData(0, 0, W, H);
                },
                getDelayMs: (i) => (i + 1 < T.length ? T[i + 1] : total) - T[i],
                loop: true,
            });
        };

        try {
            let blob = null;
            for (const maxWidth of [1080, 800, 600]) {
                blob = await renderGif(maxWidth);
                if (blob.size <= 9.5 * 1024 * 1024) break; // Cloudinary upload limit safety
            }
            return await new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onload = () => resolve(fr.result);
                fr.onerror = reject;
                fr.readAsDataURL(blob);
            });
        } catch (e) {
            console.error('exportAnimatedPageAsDataURL failed:', e);
            return null;
        }
    };

    const exportPageAsDataURL = (pageIdx) => {
        if (pageHasAnimation(pages[pageIdx])) return exportAnimatedPageAsDataURL(pageIdx);
        return new Promise((resolve) => {
            const page = pages[pageIdx];
            if (!page || !page.layout) { resolve(null); return; }

            const canvas = document.createElement('canvas');
            const pageWidth = page.pageWidth || project.pageWidth || 1200;
            const pageHeight = page.pageHeight || project.pageHeight || 1600;
            canvas.width = pageWidth;
            canvas.height = pageHeight;
            const ctx = canvas.getContext('2d');

            ctx.fillStyle = page.borderColor || '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Helper: load image with CORS, retry with cache-bust on failure
            const loadImgRetry = (src) => {
                return new Promise((res) => {
                    const img = new Image();
                    img.crossOrigin = "Anonymous";
                    img.onload = () => res(img);
                    img.onerror = () => {
                        const img2 = new Image();
                        img2.crossOrigin = "Anonymous";
                        img2.onload = () => res(img2);
                        img2.onerror = () => res(null);
                        const bustChar = src.includes('?') ? '&' : '?';
                        img2.src = src + bustChar + '_cb=' + Date.now();
                    };
                    img.src = src;
                });
            };

            const assets = page.panelAssets || {};
            const loadPromises = page.layout.panels.map(panel => {
                return new Promise(async (res) => {
                    const asset = assets[panel.id];
                    let src = null;
                    if (typeof asset === 'string') src = asset;
                    else if (asset && typeof asset === 'object') src = asset.preview || asset.fullImage;

                    if (src) {
                        const img = await loadImgRetry(src);
                        if (img) {
                            ctx.save();
                            if (panel.points && panel.points.length > 0) {
                                ctx.beginPath();
                                panel.points.forEach((pt, idx) => {
                                    const px = (pt.x / 100) * panel.width + panel.x;
                                    const py = (pt.y / 100) * panel.height + panel.y;
                                    if (idx === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
                                });
                                ctx.closePath(); ctx.clip();
                            } else {
                                ctx.beginPath(); ctx.rect(panel.x, panel.y, panel.width, panel.height); ctx.clip();
                            }
                            const iw = img.naturalWidth, ih = img.naturalHeight;
                            const coverScale = Math.max(panel.width / iw, panel.height / ih);
                            const sw = iw * coverScale, sh = ih * coverScale;
                            ctx.drawImage(img, panel.x + (panel.width - sw) / 2, panel.y + (panel.height - sh) / 2, sw, sh);
                            if (panel.points && panel.points.length > 0) {
                                ctx.strokeStyle = '#000000'; ctx.lineWidth = 4; ctx.stroke();
                            } else {
                                ctx.strokeStyle = '#000000'; ctx.lineWidth = 4;
                                ctx.strokeRect(panel.x, panel.y, panel.width, panel.height);
                            }
                            ctx.restore();
                        }
                        res();
                    } else { res(); }
                });
            });

            Promise.all(loadPromises).then(() => {
                try {
                    resolve(canvas.toDataURL('image/png'));
                } catch (e) {
                    console.error('exportPageAsDataURL toDataURL failed:', e);
                    resolve(null);
                }
            });
        });
    };

    // 
    // PUBLISH COMIC (new or existing)
    // 
    const handlePublish = async () => {
        if (publishMode === 'new' && !publishData.title.trim()) {
            toast.error("Please enter a comic title"); return;
        }
        if (publishMode === 'existing' && !selectedComicId) {
            toast.error("Please select a comic"); return;
        }

        setIsPublishing(true);
        const toastId = toast.loading(`Exporting ${pages.length} page(s) and publishing...`);

        try {
            // Step 1: Export all pages as data URLs
            const dataURLs = [];
            for (let i = 0; i < pages.length; i++) {
                toast.loading(`Rendering page ${i + 1}/${pages.length}...`, { id: toastId });
                const dataURL = await exportPageAsDataURL(i);
                if (dataURL) dataURLs.push(dataURL);
            }

            if (dataURLs.length === 0) {
                toast.error("No pages with content to publish!", { id: toastId });
                setIsPublishing(false); return;
            }

            // Step 2: Upload all data URLs to Cloudinary
            toast.loading(`Uploading ${dataURLs.length} page(s)...`, { id: toastId });
            const uploadedURLs = [];
            for (const dataURL of dataURLs) {
                const url = await uploadBase64Image(dataURL);
                uploadedURLs.push(url);
            }

            // Step 3: Create comic or add to existing
            if (publishMode === 'new') {
                const payload = {
                    title: publishData.title,
                    description: publishData.description || '',
                    categories: publishData.categories || [],
                    tags: publishData.tags || [],
                    tips_enabled: true,
                    episodes: [{ title: 'Episode 1', slides: uploadedURLs }]
                };
                const res = await axios.post(`${API}/comics/streamlined`, payload, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                toast.success(`🎉 Comic "${publishData.title}" published with ${uploadedURLs.length} pages!`, { id: toastId });
                setShowPublishModal(false);
                navigate(`/streamlined-comic/${res.data.id}`);
            } else {
                const payload = {
                    slides: uploadedURLs,
                };
                if (selectedEpisodeId && selectedEpisodeId !== 'new') {
                    // Append to existing episode
                    payload.episode_id = selectedEpisodeId;
                } else {
                    // Create new episode
                    payload.episode_title = episodeTitle || 'New Episode';
                }
                const res = await axios.post(`${API}/comics/streamlined/${selectedComicId}/add-pages`, payload, { headers: { Authorization: `Bearer ${token}` } });
                const modeMsg = res.data.mode === 'appended' ? 'added to episode' : 'published as new episode';
                toast.success(`🎉 ${uploadedURLs.length} pages ${modeMsg}!`, { id: toastId });
                setShowPublishModal(false);
                navigate(`/streamlined-comic/${selectedComicId}`);
            }
        } catch (err) {
            console.error('Publish error:', err);
            toast.error(`Publish failed: ${err.response?.data?.detail || err.message}`, { id: toastId });
        } finally {
            setIsPublishing(false);
        }
    };

    // 
    // ADD PAGE WIZARD HELPERS
    // 
    const getAddPageDimensions = () => {
        // pageWidth and pageHeight already reflect the project's orientation
        // (set correctly by CreateComicFlow: landscape = 1600x1200, portrait = 1200x1600)
        return addPageOrientation === 'landscape'
            ? { w: 1600, h: 1200 }
            : { w: 1200, h: 1600 };
    };

    const openAddPageModal = () => {
        // Gate: guests get 1 free page, then must sign in
        if (isLanding && !token && pages.length >= 1) {
            requireAuth('add more pages', 'add more pages');
            return;
        }
        setAddPageStep(1);
        setSelectedNewLayout(null);
        setAddPageOrientation(project.orientation || 'portrait');
        setAddPageStory('');
        setAddPageStyle(project.artStyle || '');
        setAddPageChars(project.characterDescriptions || '');
        setAddPageGenMethod('image');
        setAddPageModel('caravaggio');
        setShowCustomLayoutDesigner(false);
        setShowAddPageModal(true);
    };

    const addBlankPage = (layout) => {
        const l = layout || selectedNewLayout;
        if (!l) { toast.error('Please select a layout first'); return; }
        const dimensions = getAddPageDimensions();
        const newPage = {
            layout: l,
            pageWidth: dimensions.w,
            pageHeight: dimensions.h,
            orientation: addPageOrientation,
            panelAssets: {},
            rawPanelAssets: {},
            borderColor: '#ffffff'
        };
        setPages(prev => [...prev, newPage]);
        setCurrentPageIndex(pages.length);
        setShowAddPageModal(false);
        setShowCustomLayoutDesigner(false);
        toast.success(`Page ${pages.length + 1} added (blank)!`);
    };

    const generateForPage = async (layout) => {
        const l = layout || selectedNewLayout;
        if (!l) { toast.error('Please select a layout first'); return; }
        if (!addPageStory.trim()) { toast.error('Please enter a story/description'); return; }

        // Add the page first (blank)
        const pageIdx = pages.length;
        const dimensions = getAddPageDimensions();
        const newPage = {
            layout: l,
            pageWidth: dimensions.w,
            pageHeight: dimensions.h,
            orientation: addPageOrientation,
            panelAssets: {},
            rawPanelAssets: {},
            borderColor: '#ffffff'
        };
        setPages(prev => [...prev, newPage]);
        setCurrentPageIndex(pageIdx);
        setShowAddPageModal(false);
        setIsPageGenerating(true);

        const panels = l.panels;
        const isVideoMode = addPageGenMethod === 'video';

        setPageGenProgress({
            step: isVideoMode ? 'Preparing story for video...' : 'Breaking story into scenes...',
            current: 0,
            total: panels.length,
            panelStatuses: panels.map(p => ({ id: p.id, status: 'waiting' }))
        });

        try {
            // Step 1: Break story into panel scenes (EXACT same as runAutoGeneration)
            const storyRes = await axios.post(`${API}/ai/story-to-panels`, {
                story: addPageStory,
                panel_count: panels.length,
                style: addPageStyle || null,
                characters: addPageChars || null
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000 // 2 min timeout for story decomposition
            });

            if (!storyRes.data.success || !storyRes.data.panels) {
                throw new Error('Failed to decompose story');
            }

            const sceneDescriptions = storyRes.data.panels;

            // Step 2: Build panel specs with dimensions + scenes (EXACT same format)
            const panelSpecs = panels.map((panel, idx) => {
                const scene = sceneDescriptions[idx];
                return {
                    panel_id: String(panel.id),
                    scene_description: scene ? `${scene.scene} Camera: ${scene.camera}. Mood: ${scene.mood}.` : `Panel ${idx + 1}`,
                    width: Math.round(panel.width),
                    height: Math.round(panel.height)
                };
            });

            // Step 3: Generate panels using selected method (EXACT same endpoints as runAutoGeneration)
            let batchRes;

            if (isVideoMode) {
                //  VIDEO MODE: Generate video  extract frames 
                setPageGenProgress(prev => ({
                    ...prev,
                    step: '🎬 Generating video & stylizing panels (~3-5 min)...',
                    panelStatuses: panels.map(p => ({ id: p.id, status: 'generating' }))
                }));

                batchRes = await axios.post(`${API}/ai/video-generate-panels`, {
                    panels: panelSpecs,
                    story: addPageStory,
                    style: addPageStyle || '',
                    characters: addPageChars || '',
                    frame_strategy: 'scene_change'
                }, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 600000 // 10 min timeout for video generation
                });

                // Step 4: Process video results
                if (batchRes && batchRes.data.success && batchRes.data.panels) {
                    const newAssets = {};
                    const updatedStatuses = [];
                    const newRawAssets = {};

                    batchRes.data.panels.forEach(result => {
                        if (result.success && result.image_url) {
                            newAssets[result.panel_id] = result.image_url;
                            updatedStatuses.push({ id: result.panel_id, status: 'done' });
                            if (result.raw_image_url) {
                                newRawAssets[result.panel_id] = result.raw_image_url;
                            }
                        } else {
                            updatedStatuses.push({ id: result.panel_id, status: 'failed' });
                        }
                    });

                    setPages(prev => prev.map((p, i) => i === pageIdx
                        ? { ...p, panelAssets: newAssets, rawPanelAssets: newRawAssets }
                        : p
                    ));

                    setPageGenProgress(prev => ({ ...prev, step: 'Complete!', panelStatuses: updatedStatuses }));
                    const successCount = batchRes.data.panels.filter(p => p.success).length;
                    toast.success(`🎬 Video: Generated ${successCount}/${panels.length} panels! (${batchRes.data.credits_used} credits used)`);
                    fetchCredits();
                }

            } else {
                // 📸 IMAGE MODE: Generate one image per panel sequentially
                const newAssets = {};
                const newRawAssets = {};
                const currentStatuses = panels.map(p => ({ id: String(p.id), status: 'waiting' }));

                setPageGenProgress(prev => ({
                    ...prev,
                    step: 'Initializing generation...',
                    panelStatuses: currentStatuses
                }));

                // ── CHARACTER STUDIO: Build consistency data ──
                // NOTE: No shared seed — each panel uses random seed for unique compositions.
                // Character consistency is handled by IP-Adapter/PuLID face-lock, not seed.

                // Build structured character text from character gallery
                const characterText = characters.length > 0
                    ? characters.map(c =>
                        `CHARACTER "${c.name}": ${c.description}. MAINTAIN THIS EXACT APPEARANCE IN EVERY PANEL.`
                    ).join('\n')
                    : (addPageChars || '');

                // Get primary anchor image for IP-Adapter face-lock
                const anchorChar = characters.find(c => c.imageBase64 || c.imageUrl);
                const anchorImageBase64 = anchorChar?.imageBase64 || anchorChar?.imageUrl || null;

                // Determine if face-lock is active
                const useFaceLock = faceLockEnabled && !!anchorImageBase64 && addPageModel === 'caravaggio';

                let successCount = 0;
                let creditsUsed = 0;

                for (let i = 0; i < panelSpecs.length; i++) {
                    const spec = panelSpecs[i];
                    
                    // Update status of current panel to generating
                    currentStatuses[i].status = 'generating';
                    setPageGenProgress(prev => ({
                        ...prev,
                        step: `Generating Panel ${i + 1} of ${panelSpecs.length}${useFaceLock ? ' 🔒' : ''}...`,
                        panelStatuses: [...currentStatuses]
                    }));

                    try {
                        const genRequest = () => axios.post(`${API}/ai/batch-generate-panels`, {
                            panels: [spec],
                            style: addPageStyle || '',
                            characters: characterText,
                            character_reference_image: useFaceLock ? anchorImageBase64 : null,
                            page_seed: null,
                            face_lock: useFaceLock,
                            model_engine: addPageModel || 'gemini',
                            ip_adapter_scale: costumeLockStrength
                        }, {
                            headers: { Authorization: `Bearer ${token}` },
                            timeout: 300000 // 5 min timeout per panel
                        });

                        let response = null;
                        let panelSuccess = false;

                        // First attempt
                        try {
                            response = await genRequest();
                        } catch (firstErr) {
                            console.warn(`Panel ${spec.panel_id} attempt 1 failed: ${firstErr.message}. Retrying in 3s...`);
                            // Auto-retry after 3s
                            currentStatuses[i].status = 'retrying';
                            setPageGenProgress(prev => ({
                                ...prev,
                                step: `Retrying Panel ${i + 1}...`,
                                panelStatuses: [...currentStatuses]
                            }));
                            await new Promise(r => setTimeout(r, 3000));
                            try {
                                response = await genRequest();
                            } catch (retryErr) {
                                console.error(`Panel ${spec.panel_id} retry also failed:`, retryErr.message);
                            }
                        }

                        if (response?.data?.success && response?.data?.panels?.[0]) {
                            const result = response.data.panels[0];
                            if (result.success && result.image_url) {
                                newAssets[result.panel_id] = result.image_url;
                                if (result.raw_image_url) {
                                    newRawAssets[result.panel_id] = result.raw_image_url;
                                }
                                currentStatuses[i].status = 'done';
                                successCount++;
                                creditsUsed += (response.data.credits_used || 1);

                                // LIVE RENDERING: Update the page assets IMMEDIATELY
                                setPages(prev => prev.map((p, index) => index === pageIdx
                                    ? { ...p, panelAssets: { ...newAssets }, rawPanelAssets: { ...newRawAssets } }
                                    : p
                                ));

                                // Store the scene description used for this panel (for regen pre-fill)
                                // Store prompt under the correct page index (pageIdx, not currentPageIndex)
                                setPanelPrompts(prev => ({
                                    ...prev,
                                    [pageIdx]: {
                                        ...(prev[pageIdx] || {}),
                                        [spec.panel_id]: spec.scene_description
                                    }
                                }));

                                fetchCredits();
                                panelSuccess = true;
                            }
                        }

                        if (!panelSuccess) {
                            currentStatuses[i].status = 'failed';
                        }
                    } catch (err) {
                        console.error(`Error generating panel ${spec.panel_id}:`, err);
                        currentStatuses[i].status = 'failed';
                    }

                    // Update UI status list
                    setPageGenProgress(prev => ({
                        ...prev,
                        panelStatuses: [...currentStatuses]
                    }));
                }

                setPageGenProgress(prev => ({
                    ...prev,
                    step: 'Complete!',
                    panelStatuses: [...currentStatuses]
                }));

                toast.success(`Generated ${successCount}/${panels.length} panels! (${creditsUsed} credits used)`);
                trackAIGeneration({ modelEngine: addPageModel || 'gemini', success: true, panelCount: successCount, source: 'add_page' });
            }

        } catch (error) {
            console.error('Page generation error:', error);
            const msg = error.response?.data?.detail || error.message || 'Generation failed';
            toast.error(msg);
        } finally {
            setTimeout(() => setIsPageGenerating(false), 1500);
        }
    };

    // Handle custom layout save from LayoutDesigner
    const handleCustomLayoutSave = (panels) => {
        const customLayout = {
            id: `custom-${Date.now()}`,
            name: 'Custom Layout',
            panels: panels.map((p, i) => ({
                id: p.isBackground ? 'p-1' : `p-${i + 1}`,
                x: p.x,
                y: p.y,
                width: p.width,
                height: p.height,
                isBackground: p.isBackground || false,
                zIndex: p.zIndex || (p.isBackground ? 0 : 10),
                color: p.color,
                points: p.points || [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 }]
            }))
        };
        setSelectedNewLayout(customLayout);
        setShowCustomLayoutDesigner(false);
        setAddPageStep(2);
    };

    const isLanding = renderMode === 'landing';
    const requireAuth = async (action, pendingAction = null) => {
        if (!token) {
            // Save current work before redirecting
            if (isLanding && pages.length > 0) {
                await saveWorkToSession(pendingAction || action);
            }
            if (onAuthRequired) onAuthRequired(`Sign in to ${action}`);
            else { toast.info(`Sign in to ${action}`); navigate('/login', { state: { returnTo: '/' } }); }
            return false;
        }
        return true;
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white flex flex-col h-screen overflow-hidden">

            {/* Landing Nav Bar €” site-wide navigation */}
            {isLanding && (
                <div className="h-11 bg-gray-900/95 border-b border-gray-800/60 flex items-center justify-between px-5 shrink-0 z-20 backdrop-blur-sm">
                    <div className="flex items-center gap-4">
                        <a href="/" className="flex items-center gap-2 group">
                            <div className="relative flex items-center overflow-hidden rounded-lg">
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-cyan-400 opacity-30 blur-md" />
                                <img src="/favicon_new.png" className="relative z-10 w-6 h-6" alt="ComicMenia" />
                            </div>
                            <span className="text-base font-bold bg-gradient-to-r from-purple-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent">ComicMenia</span>
                        </a>
                        <div className="h-4 w-px bg-gray-700" />
                        <nav className="flex items-center gap-1">
                            <button onClick={() => navigate('/comics')} className="px-3 py-1 rounded-md text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 transition">Comics</button>
                            <button onClick={() => user ? navigate('/dashboard') : requireAuth('access your profile')} className="px-3 py-1 rounded-md text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 transition">Creator Profile</button>
                            <button onClick={() => navigate('/community')} className="px-3 py-1 rounded-md text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 transition">Community</button>
                            <button onClick={() => navigate('/store')} className="px-3 py-1 rounded-md text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 transition">Store</button>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        {credits !== null && (
                            <span className="flex items-center gap-1 rounded-md border border-purple-500/30 bg-purple-500/10 px-2.5 py-1 text-xs font-semibold text-purple-300">
                                <Zap className="h-3 w-3" />{credits}
                            </span>
                        )}
                        {user ? (
                            <button onClick={() => navigate('/dashboard')} className="h-7 w-7 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-xs font-bold text-white flex items-center justify-center" title={user.display_name || 'Profile'}>
                                {user.display_name?.charAt(0)?.toUpperCase() || 'U'}
                            </button>
                        ) : (
                            <button onClick={() => navigate('/login')} className="rounded-md bg-white/10 hover:bg-white/15 px-3 py-1 text-xs font-semibold text-white transition">Sign In</button>
                        )}
                    </div>
                </div>
            )}

            {/* READ-ONLY BANNER OVERLAY */}
            {isReadOnly && (
                <div className="bg-gradient-to-r from-emerald-950 via-teal-900 to-purple-950 border-b border-emerald-500/40 text-white px-6 py-2.5 flex items-center justify-between text-xs font-semibold shadow-xl relative z-20">
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                        <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded font-bold uppercase tracking-wider text-[10px]">
                            👁 Read-Only Blueprint Preview
                        </span>
                        <span className="text-gray-300 pl-2">
                            Exploring <strong className="text-white font-bold">{project?.title || 'Public Artwork'}</strong> {project?.author_name ? `by @${project.author_name}` : ''}. Original project is protected.
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            size="sm"
                            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-xs h-7 px-3.5 rounded-lg shadow-md shadow-emerald-500/20"
                            onClick={() => {
                                if (!token) {
                                    if (onAuthRequired) onAuthRequired('clone this project');
                                    else toast.error('Please sign in to save a copy of this project');
                                    return;
                                }
                                // Clone project as user's own project
                                setProject(prev => ({
                                    ...prev,
                                    id: null,
                                    title: `${prev?.title || 'Artwork'} (Copy)`,
                                    is_public: false
                                }));
                                setShowNameProjectModal(true);
                                toast.success('Cloned project! Save your copy to your Creator Dashboard.');
                            }}
                        >
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> Clone / Save My Copy
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-white h-7 text-xs px-2"
                            onClick={() => navigate(-1)}
                        >
                            <X className="w-4 h-4 mr-1" /> Exit Preview
                        </Button>
                    </div>
                </div>
            )}

            {/* Header Toolbar */}
            <header className="h-16 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-6 z-10 shrink-0">
                <div className="flex items-center space-x-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/create-comic-flow')} className="text-gray-400">
                        <ChevronLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <div className="h-6 w-px bg-gray-700" />
                    {editingTitle ? (
                        <input
                            ref={titleInputRef}
                            autoFocus
                            className="text-lg font-bold text-gray-100 bg-gray-800 border border-purple-500 rounded-lg px-3 py-1 outline-none focus:ring-2 focus:ring-purple-500/50 w-64"
                            defaultValue={project.title || 'Untitled Comic'}
                            onBlur={(e) => { setProject(p => ({ ...p, title: e.target.value || 'Untitled Comic' })); setEditingTitle(false); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { setProject(p => ({ ...p, title: e.target.value || 'Untitled Comic' })); setEditingTitle(false); } if (e.key === 'Escape') setEditingTitle(false); }}
                        />
                    ) : (
                        <h1 className="text-lg font-bold text-gray-100 cursor-pointer hover:text-purple-300 transition group flex items-center gap-2" onClick={() => setEditingTitle(true)}>
                            {project.title || 'Untitled Comic'}
                            <Pencil className="w-3 h-3 text-gray-500 opacity-0 group-hover:opacity-100 transition" />
                        </h1>
                    )}
                    <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
                        {project.panelCount} Panels | {project.orientation}
                    </span>
                </div>

                <div className="flex items-center space-x-2">
                    {/* + New Project Button */}
                    <Button
                        variant="outline"
                        className="border-gray-700 text-gray-300 hover:border-green-500/50 hover:text-green-300"
                        onClick={() => {
                            if (Object.keys(panelAssets).length > 0 || pages.some(p => Object.keys(p.panelAssets || {}).length > 0)) {
                                if (!window.confirm('Start a new project? Any unsaved work will be lost.')) return;
                            }
                            // Reset all state to fresh
                            setProject({
                                title: 'Untitled Comic',
                                pageWidth: 1200,
                                pageHeight: 1600,
                                orientation: 'portrait',
                                panelCount: 4,
                                story: '',
                                artStyle: '',
                                characterDescriptions: ''
                            });
                            setPages([]);
                            setPanelAssets({});
                            setRawPanelAssets({});
                            setCurrentPageIndex(0);
                            setMatchStyleBackup(null);
                            setIsMatchMode(false);
                            // Clear IndexedDB session
                            openSessionDB().then(db => {
                                const tx = db.transaction(IDB_STORE, 'readwrite');
                                tx.objectStore(IDB_STORE).delete(IDB_KEY);
                                tx.oncomplete = () => db.close();
                                tx.onerror = () => db.close();
                            }).catch(() => {});
                            toast.success('New project started!');
                        }}
                    >
                        <Plus className="w-4 h-4 mr-2" /> New
                    </Button>

                    {/* Raw/Styled Toggle -- only show when raw frames are available */}
                    {Object.keys(rawPanelAssets).length > 0 && (
                        <Button
                            variant="outline"
                            className={`border-gray-700 ${showRawFrames
                                ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                                : 'text-gray-300 hover:border-blue-500/50 hover:text-blue-300'
                                }`}
                            onClick={() => setShowRawFrames(!showRawFrames)}
                            disabled={isAutoGenerating}
                        >
                            {showRawFrames ? (
                                <><EyeOff className="w-4 h-4 mr-2" /> Styled</>
                            ) : (
                                <><Eye className="w-4 h-4 mr-2" /> Raw</>
                            )}
                        </Button>
                    )}

                    {/* Match Style Button -- only show when panels have images */}
                    {Object.keys(panelAssets).length > 1 && (
                        <Button
                            variant="outline"
                            className={`border-gray-700 ${isMatchMode
                                ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                                : 'text-gray-300 hover:border-purple-500/50 hover:text-purple-300'
                                }`}
                            onClick={() => {
                                if (isMatchMode) {
                                    setIsMatchMode(false); // cancel selection mode
                                } else {
                                    setIsMatchMode(true);  // enter selection mode
                                    toast.info('Click a panel to use as style reference');
                                }
                            }}
                            disabled={isMatching || isAutoGenerating}
                        >
                            {isMatching ? (
                                <span className="flex items-center"><Loader2 className="animate-spin mr-2 h-4 w-4" /> Matching...</span>
                            ) : isMatchMode ? (
                                <><Palette className="w-4 h-4 mr-2" /> Cancel</>
                            ) : (
                                <><Palette className="w-4 h-4 mr-2" /> Match Style</>
                            )}
                        </Button>
                    )}
                    {/* Undo Match Style */}
                    {matchStyleBackup && (
                        <Button
                            variant="outline"
                            className="border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                            onClick={async () => {
                                setPanelAssets(matchStyleBackup);
                                setMatchStyleBackup(null);
                                try {
                                    await axios.post(`${API}/ai/credits/refund-match-style`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                    fetchCredits();
                                    toast.success('Match Style undone -- 1 credit refunded!');
                                } catch (e) {
                                    console.error('Refund error:', e);
                                    toast.success('Match Style undone (credit refund failed)');
                                }
                            }}
                        >
                            <Undo2 className="w-4 h-4 mr-2" /> Undo Match
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        className="border-gray-700 text-gray-300"
                        onClick={() => {
                            if (isLanding && !requireAuth('save your draft')) return;
                            // If no project ID yet (first save), show name modal
                            if (!project.id) {
                                setShowNameProjectModal(true);
                            } else {
                                handleSaveDraft();
                            }
                        }}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <span className="flex items-center"><div className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> Saving...</span>
                        ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Draft</>
                        )}
                    </Button>
                    <Button onClick={handleExport} className="bg-purple-600 hover:bg-purple-700">
                        <Download className="w-4 h-4 mr-2" /> Export Page
                    </Button>
                    <Button onClick={() => {
                        if (isLanding && !requireAuth('publish your comic')) return;
                        setShowPublishModal(true);
                        setPublishData({ title: project.title || '', description: '', categories: [], tags: [] });
                        // Fetch user's comics for the "existing" tab
                        axios.get(`${API}/comics/my-streamlined`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(r => {
                                console.log('[Publish] Fetched my comics:', r.data);
                                setMyComics(r.data || []);
                            })
                            .catch(err => {
                                console.error('[Publish] Failed to fetch comics:', err.response?.status, err.response?.data || err.message);
                                toast.error(`Failed to load your comics: ${err.response?.data?.detail || err.message}`);
                            });
                    }} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                        <Send className="w-4 h-4 mr-2" /> Publish Comic
                    </Button>

                    {/* Panel Border Color Picker */}
                    <div className="relative" ref={colorPickerRef}>
                        <Button
                            variant="outline"
                            className="border-gray-700 text-gray-300 hover:border-purple-500/50"
                            onClick={() => setShowColorPicker(!showColorPicker)}
                            title="Panel border color"
                        >
                            <div
                                className="w-4 h-4 rounded-sm mr-2 border border-gray-500"
                                style={{ backgroundColor: borderColor }}
                            />
                            Border
                        </Button>
                        {showColorPicker && (
                            <div className="absolute right-0 top-full mt-2 bg-gray-900 border border-gray-700 rounded-xl p-3 shadow-2xl z-50 w-48">
                                <p className="text-xs text-gray-400 mb-2 font-medium">Border / Gutter Color</p>
                                <div className="grid grid-cols-6 gap-1.5 mb-3">
                                    {['#ffffff', '#000000', '#1a1a2e', '#f5f5dc', '#e8d5b7', '#2d2d2d',
                                        '#ff4444', '#ff8800', '#ffcc00', '#44bb44', '#4488ff', '#aa44ff'].map(c => (
                                            <button
                                                key={c}
                                                className={`w-6 h-6 rounded-md border-2 transition-transform hover:scale-110 ${borderColor === c ? 'border-purple-400 ring-1 ring-purple-400' : 'border-gray-600'
                                                    }`}
                                                style={{ backgroundColor: c }}
                                                onClick={() => { setBorderColor(c); setShowColorPicker(false); }}
                                            />
                                        ))}
                                </div>
                                <label className="flex items-center gap-2 text-xs text-gray-400">
                                    Custom:
                                    <input
                                        type="color"
                                        value={borderColor}
                                        onChange={(e) => setBorderColor(e.target.value)}
                                        className="w-8 h-6 rounded cursor-pointer bg-transparent border-0"
                                    />
                                    <span className="text-gray-500 font-mono">{borderColor}</span>
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Credit Counter */}
                    {credits !== null && (
                        <button
                            onClick={() => navigate('/credits')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-semibold transition-all duration-200 cursor-pointer hover:scale-105 ${credits < 3
                                ? 'bg-red-900/40 border-red-500/50 text-red-300'
                                : 'bg-gray-800 border-purple-500/40 text-purple-300 hover:border-purple-400'
                                }`}
                            title="Click to buy more credits"
                        >
                            <Zap className="w-4 h-4" />
                            <span>{credits}</span>
                            {credits < 3 && <span className="text-xs text-red-400">⚠️</span>}
                        </button>
                    )}
                </div>
            </header>

            {/* Main Workspace — Infinite Canvas (All Pages) */}
            <main className="flex-1 overflow-auto bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-gray-950 relative p-8">

                {/* AI Auto-Generation Progress Overlay (page 0 initial) */}
                {isAutoGenerating && (
                    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center">
                        <div className="bg-gray-900 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl shadow-purple-500/10">
                            <div className="text-center mb-6">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 mb-4 animate-pulse">
                                    <span className="text-2xl">🎨</span>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-1">AI Generating Your Comic</h3>
                                <p className="text-sm text-gray-400">{generationProgress.step}</p>
                            </div>

                            {/* Panel Progress Cards */}
                            <div className="space-y-2 mb-6">
                                {generationProgress.panelStatuses.map((ps, idx) => (
                                    <div key={ps.id} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-300 ${ps.status === 'done' ? 'bg-green-500/10 border border-green-500/30' :
                                        ps.status === 'generating' ? 'bg-purple-500/10 border border-purple-500/30' :
                                            ps.status === 'failed' ? 'bg-red-500/10 border border-red-500/30' :
                                                'bg-gray-800/50 border border-gray-700/30'
                                        }`}>
                                        <span className="text-gray-300">Panel {idx + 1}</span>
                                        <span className={`text-xs font-medium ${ps.status === 'done' ? 'text-green-400' :
                                            ps.status === 'generating' ? 'text-purple-400' :
                                                ps.status === 'failed' ? 'text-red-400' :
                                                    'text-gray-500'
                                            }`}>
                                            {ps.status === 'done' ? '✓ Done' :
                                                ps.status === 'generating' ? '✨ Generating...' :
                                                    ps.status === 'failed' ? '✗ Failed' :
                                                        'Waiting...'}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 animate-pulse" style={{ width: '100%' }} />
                            </div>
                            <p className="text-center text-xs text-gray-500 mt-3">This may take a minute -- sit tight!</p>
                        </div>
                    </div>
                )}

                {/* Infinite Vertical Canvas: All Pages */}
                {pages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32">
                        <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-2xl border-2 border-dashed border-gray-600 bg-gray-800/50">
                            <Plus className="h-10 w-10 text-gray-500" />
                        </div>
                        <h2 className="mb-2 text-2xl font-bold text-gray-200">Start Your Comic</h2>
                        <p className="mb-6 max-w-md text-center text-sm text-gray-500">Add your first page to begin creating. Choose a layout, then fill panels with AI generation or draw manually with ComicaPro.</p>
                        <button onClick={openAddPageModal} className="rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all hover:scale-105">
                            <Plus className="mr-2 inline h-4 w-4" />Add First Page
                        </button>
                    </div>
                ) : (
                <div className="flex flex-col items-center gap-12">
                    {pages.map((page, pageIdx) => {
                        const isActive = pageIdx === currentPageIndex;
                        const pgAssets = page.panelAssets || {};
                        const pgRawAssets = page.rawPanelAssets || {};
                        const pgBorderColor = page.borderColor || '#ffffff';
                        const pageWidth = page.pageWidth || project.pageWidth || 1200;
                        const pageHeight = page.pageHeight || project.pageHeight || 1600;
                        const pageScale = Math.min(maxW / pageWidth, maxH / pageHeight);
                        const pageContainerWidth = pageWidth * pageScale;
                        const pageContainerHeight = pageHeight * pageScale;

                        return (
                            <div key={pageIdx} className="relative group/page" onClick={() => setCurrentPageIndex(pageIdx)}>
                                {/* Page Badge */}
                                <div className={`absolute -top-4 left-4 z-20 px-3 py-1 rounded-full text-xs font-bold shadow-lg transition-all ${isActive
                                    ? 'bg-purple-600 text-white shadow-purple-500/30'
                                    : 'bg-gray-800 text-gray-400 border border-gray-700'
                                    }`}>
                                    Page {pageIdx + 1}
                                </div>

                                {/* Delete Page Button */}
                                {pages.length > 1 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Delete page ${pageIdx + 1}?`)) {
                                                setPages(prev => prev.filter((_, i) => i !== pageIdx));
                                                if (currentPageIndex >= pageIdx && currentPageIndex > 0) {
                                                    setCurrentPageIndex(prev => prev - 1);
                                                }
                                            }
                                        }}
                                        className="absolute -top-2 -right-2 z-20 w-7 h-7 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover/page:opacity-100 transition-opacity hover:scale-110 hover:bg-red-500 shadow-lg"
                                    >
                                        <X className="w-4 h-4 text-white" />
                                    </button>
                                )}

                                {/* The Page Canvas */}
                                <div
                                    className={`relative shadow-2xl transition-all duration-300 cursor-pointer ${isActive
                                        ? 'ring-2 ring-purple-500/50 shadow-purple-500/20'
                                        : 'ring-1 ring-gray-800 hover:ring-gray-600'
                                        }`}
                                    style={{
                                            width: pageContainerWidth,
                                            height: pageContainerHeight,
                                        backgroundColor: pgBorderColor,
                                        boxShadow: isActive ? '0 0 60px rgba(147,51,234,0.15)' : '0 0 30px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    {/* Render Panels for this page */}
                                    {page.layout && page.layout.panels.map((panel) => {
                                        const hasContent = !!pgAssets[panel.id];

                                        return (
                                            <div
                                                key={panel.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentPageIndex(pageIdx);
                                                    if (isMatchMode && hasContent) {
                                                        handleMatchStyleSelect(panel);
                                                    } else if (!isMatchMode) {
                                                        handlePanelClick(panel);
                                                    }
                                                }}
                                                className={`absolute group cursor-pointer overflow-hidden transition-all duration-200 
                                                    ${isMatchMode && hasContent
                                                        ? 'border-3 border-amber-400/70 hover:border-amber-400 ring-2 ring-amber-400/30'
                                                        : panel.zIndex > 0
                                                            ? 'border-4 shadow-2xl z-10 hover:scale-[1.02] hover:shadow-purple-500/50'
                                                            : `border-2 ${hasContent ? 'border-transparent' : 'border-dashed border-gray-300'} hover:border-purple-400`
                                                    }
                                                `}
                                                style={{
                                                    left: panel.x * pageScale,
                                                    top: panel.y * pageScale,
                                                    width: panel.width * pageScale,
                                                    height: panel.height * pageScale,
                                                    backgroundColor: hasContent ? pgBorderColor : panel.color,
                                                    borderColor: panel.zIndex > 0 ? pgBorderColor : undefined,
                                                    zIndex: panel.zIndex || 0,
                                                    clipPath: panel.clipPath !== 'none' ? panel.clipPath : undefined,
                                                    filter: (panel.clipPath && panel.clipPath !== 'none') ? 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' : undefined
                                                }}
                                            >
                                                {/* Content Image */}
                                                {hasContent ? (
                                                    <img
                                                        crossOrigin="anonymous"
                                                        src={
                                                            showRawFrames && pgRawAssets[panel.id]
                                                                ? pgRawAssets[panel.id]
                                                                : getPanelPreview(pgAssets[panel.id])
                                                        }
                                                        alt={`Panel ${panel.id}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 opacity-50 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all">
                                                        <Edit3 className="w-8 h-8 mb-2 text-purple-600" />
                                                        <span className="font-bold text-sm text-gray-600">Edit Panel {panel.id.toUpperCase()}</span>
                                                    </div>
                                                )}

                                                {/* Match Style Selection Overlay */}
                                                {isMatchMode && hasContent && (
                                                    <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center transition-opacity z-20">
                                                        <div className="bg-black/80 text-amber-300 px-3 py-2 rounded-lg text-xs font-bold shadow-lg backdrop-blur-sm border border-amber-500/30">
                                                            🎨 Use as Reference
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Normal Hover Overlay */}
                                                {!isMatchMode && (
                                                    <div className="absolute inset-0 bg-purple-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 z-20 backdrop-blur-[1px]">
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setCurrentPageIndex(pageIdx);
                                                                handlePanelClick(panel);
                                                            }}
                                                            className="bg-white hover:bg-gray-100 text-purple-700 px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-md transform translate-y-2 group-hover:translate-y-0 transition-all flex items-center gap-1.5 border border-purple-200"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" /> Edit Canvas
                                                        </button>
                                                        {hasContent && (
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setCurrentPageIndex(pageIdx);
                                                                    openRegenModal(panel, pgAssets[panel.id], pageIdx);
                                                                }}
                                                                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-md transform translate-y-2 group-hover:translate-y-0 transition-all flex items-center gap-1.5"
                                                            >
                                                                <Undo2 className="w-3.5 h-3.5 rotate-180" /> Regenerate
                                                            </button>
                                                        )}
                                                        {!hasContent && (
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setCurrentPageIndex(pageIdx);
                                                                    openRegenModal(panel, null, pageIdx);
                                                                }}
                                                                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-md transform translate-y-2 group-hover:translate-y-0 transition-all flex items-center gap-1.5"
                                                            >
                                                                <Undo2 className="w-3.5 h-3.5 rotate-180" /> Generate Panel
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/* Add Page Card (at bottom of scroll) */}
                    <button
                        onClick={openAddPageModal}
                        className="w-[300px] h-[200px] rounded-2xl border-2 border-dashed border-gray-700 hover:border-purple-400 flex flex-col items-center justify-center gap-3 transition-all hover:bg-purple-500/5 group mb-8"
                    >
                        <div className="w-14 h-14 rounded-full bg-gray-800 border-2 border-gray-600 group-hover:border-purple-400 flex items-center justify-center transition-colors">
                            <Plus className="w-7 h-7 text-gray-400 group-hover:text-purple-400" />
                        </div>
                        <span className="text-gray-400 group-hover:text-purple-300 font-semibold text-sm">Add New Page</span>
                    </button>
                </div>
                )}

            </main>

            {/* ••••••••••••••••••••••••••••••••••••••• */}
            {/* PAGE NAVIGATOR BAR */}
            {/* ••••••••••••••••••••••••••••••••••••••• */}
            <div className="h-24 bg-gray-900 border-t border-gray-800 flex items-center px-4 gap-3 shrink-0 overflow-x-auto">
                {/* ••••••••••••••••••••••••••••••••••••••• */}
                {/* NAME PROJECT MODAL (first save) */}
                {/* ••••••••••••••••••••••••••••••••••••••• */}
                {showNameProjectModal && (
                    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-gray-900 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-purple-500/20">
                            <h3 className="text-xl font-bold text-white mb-2">Name Your Project</h3>
                            <p className="text-gray-400 text-sm mb-6">Give your comic a title before saving.</p>
                            <input
                                ref={titleInputRef}
                                type="text"
                                value={project?.title || ''}
                                onChange={(e) => setProject(p => ({ ...p, title: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && project?.title?.trim()) {
                                        setShowNameProjectModal(false);
                                        handleSaveDraft();
                                    }
                                }}
                                placeholder="My Awesome Comic..."
                                className="w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-lg mb-6"
                                autoFocus
                            />
                            <div className="flex gap-3 justify-end">
                                <Button
                                    variant="outline"
                                    className="border-gray-700 text-gray-300 hover:text-white"
                                    onClick={() => setShowNameProjectModal(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold hover:shadow-lg hover:shadow-purple-500/30"
                                    disabled={!project?.title?.trim() || project?.title === 'Untitled Comic'}
                                    onClick={() => {
                                        setShowNameProjectModal(false);
                                        handleSaveDraft();
                                    }}
                                >
                                    <Save className="w-4 h-4 mr-2" /> Save Project
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <span className="text-xs text-gray-500 font-medium shrink-0">PAGES</span>
                {pages.map((page, idx) => (
                    <div
                        key={idx}
                        onClick={() => setCurrentPageIndex(idx)}
                        className={`relative group shrink-0 w-16 h-16 rounded-lg border-2 cursor-pointer transition-all duration-200 flex items-center justify-center overflow-hidden ${idx === currentPageIndex
                            ? 'border-purple-500 ring-2 ring-purple-500/30 bg-purple-500/10'
                            : 'border-gray-700 hover:border-gray-500 bg-gray-800'
                            }`}
                    >
                        <span className="text-sm font-bold text-gray-300">{idx + 1}</span>
                        {/* Delete page button */}
                        {pages.length > 1 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Delete page ${idx + 1}?`)) {
                                        setPages(prev => prev.filter((_, i) => i !== idx));
                                        if (currentPageIndex >= idx && currentPageIndex > 0) {
                                            setCurrentPageIndex(prev => prev - 1);
                                        }
                                    }
                                }}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
                            >
                                <X className="w-3 h-3 text-white" />
                            </button>
                        )}
                    </div>
                ))}
                {/* Add Page Button */}
                <button
                    onClick={openAddPageModal}
                    className="shrink-0 w-16 h-16 rounded-lg border-2 border-dashed border-gray-600 hover:border-purple-400 flex items-center justify-center transition-all text-gray-500 hover:text-purple-400 hover:bg-purple-500/5"
                >
                    <Plus className="w-6 h-6" />
                </button>
                <div className="flex-1" />
                <span className="text-xs text-gray-500 shrink-0">Page {currentPageIndex + 1} of {pages.length}</span>
            </div>


            {/*  */}
            {/* ADD PAGE WIZARD (Full-screen) */}
            {/*  */}
            {showAddPageModal && (
                <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0">
                        <div className="flex items-center gap-3">
                            <Plus className="w-5 h-5 text-purple-400" />
                            <h2 className="text-xl font-bold text-white">Add New Page</h2>
                            <span className="text-sm text-gray-500 ml-2">Step {addPageStep} of 2</span>
                        </div>
                        <button onClick={() => { setShowAddPageModal(false); setShowCustomLayoutDesigner(false); }} className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Custom Layout Designer (full-screen overlay) */}
                    {showCustomLayoutDesigner ? (
                        <div className="flex-1 overflow-hidden p-4">
                            <LayoutDesigner
                                width={getAddPageDimensions().w}
                                height={getAddPageDimensions().h}
                                onSave={handleCustomLayoutSave}
                                onCancel={() => setShowCustomLayoutDesigner(false)}
                            />
                        </div>
                    ) : addPageStep === 1 ? (
                        /* €‚€‚€‚ STEP 1: Layout Selection €‚€‚€‚ */
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="max-w-4xl mx-auto">
                                {/* Orientation Toggle */}
                                <div className="mb-6">
                                    <label className="text-sm font-semibold text-gray-300 mb-3 block">Page Orientation</label>
                                    <div className="flex gap-3">
                                        <button onClick={() => setAddPageOrientation('portrait')}
                                            className={`flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all ${addPageOrientation === 'portrait'
                                                ? 'border-purple-500 bg-purple-500/10 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                                            <div className="w-6 h-8 border-2 border-current rounded-sm" />
                                            <span className="font-medium">Portrait</span>
                                        </button>
                                        <button onClick={() => setAddPageOrientation('landscape')}
                                            className={`flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all ${addPageOrientation === 'landscape'
                                                ? 'border-purple-500 bg-purple-500/10 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                                            <div className="w-8 h-6 border-2 border-current rounded-sm" />
                                            <span className="font-medium">Landscape</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Panel Count Slider */}
                                <div className="mb-6">
                                    <label className="text-sm font-semibold text-gray-300 mb-3 block">Number of Panels: <span className="text-purple-400">{newPagePanelCount}</span></label>
                                    <input type="range" min="1" max="9" value={newPagePanelCount}
                                        onChange={(e) => setNewPagePanelCount(parseInt(e.target.value))}
                                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>1</span><span>5</span><span>9</span>
                                    </div>
                                </div>

                                {/* Layout Grid */}
                                <label className="text-sm font-semibold text-gray-300 mb-3 block">Select a Blueprint</label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                                    {/* Custom Layout Card */}
                                    <button onClick={() => setShowCustomLayoutDesigner(true)}
                                        className="border-2 border-dashed border-gray-600 hover:border-purple-400 rounded-xl p-4 transition-all flex flex-col items-center justify-center min-h-[180px] group">
                                        <div className="w-12 h-12 rounded-full bg-gray-800 border-2 border-gray-600 group-hover:border-purple-400 flex items-center justify-center mb-3 transition-colors">
                                            <Edit3 className="w-5 h-5 text-gray-400 group-hover:text-purple-400 transition-colors" />
                                        </div>
                                        <span className="text-sm font-semibold text-gray-300 group-hover:text-purple-300">Create Custom</span>
                                        <span className="text-xs text-gray-500">Draw & Skew Panels</span>
                                    </button>

                                    {/* Generated Layouts */}
                                    {generateLayouts(getAddPageDimensions().w, getAddPageDimensions().h, newPagePanelCount).map((layout) => {
                                        const dims = getAddPageDimensions();
                                        return (
                                            <button key={layout.id}
                                                onClick={() => { setSelectedNewLayout(layout); setAddPageStep(2); }}
                                                className={`border-2 rounded-xl p-3 transition-all group relative ${selectedNewLayout?.id === layout.id
                                                    ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/30'
                                                    : 'border-gray-700 hover:border-purple-400'}`}>
                                                {selectedNewLayout?.id === layout.id && (
                                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center z-10">
                                                        <CheckCircle className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div className="relative bg-gray-800 rounded-lg overflow-hidden" style={{ width: '100%', paddingBottom: `${(dims.h / dims.w) * 100}%` }}>
                                                    <div className="absolute inset-0 p-1">
                                                        {layout.panels.map((p, pi) => (
                                                            <div key={pi}
                                                                className="absolute border border-gray-500/50 rounded-sm transition-colors"
                                                                style={{
                                                                    left: `${(p.x / dims.w) * 100}%`,
                                                                    top: `${(p.y / dims.h) * 100}%`,
                                                                    width: `${(p.width / dims.w) * 100}%`,
                                                                    height: `${(p.height / dims.h) * 100}%`,
                                                                    backgroundColor: `hsl(${(pi * 60 + 120) % 360}, 40%, ${selectedNewLayout?.id === layout.id ? '35%' : '25%'})`
                                                                }}>
                                                                <span className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400 font-mono">p-{pi + 1}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-2 text-center font-medium">{layout.name}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* €‚€‚€‚ STEP 2: Generate or Blank €‚€‚€‚ */
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="max-w-2xl mx-auto">
                                {/* Selected Layout Preview */}
                                {selectedNewLayout && (
                                    <div className="mb-6 flex items-start gap-4">
                                        <div className="relative bg-gray-800 rounded-lg overflow-hidden border border-gray-700 w-32 shrink-0" style={{ paddingBottom: `${(getAddPageDimensions().h / getAddPageDimensions().w) * 32}%` }}>
                                            <div className="absolute inset-0 p-1">
                                                {selectedNewLayout.panels.map((p, pi) => (
                                                    <div key={pi} className="absolute bg-purple-500/20 border border-purple-500/40 rounded-sm" style={{
                                                        left: `${(p.x / getAddPageDimensions().w) * 100}%`,
                                                        top: `${(p.y / getAddPageDimensions().h) * 100}%`,
                                                        width: `${(p.width / getAddPageDimensions().w) * 100}%`,
                                                        height: `${(p.height / getAddPageDimensions().h) * 100}%`
                                                    }} />
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-white font-semibold">{selectedNewLayout.name}</h3>
                                            <p className="text-sm text-gray-400">{selectedNewLayout.panels.length} panels · {addPageOrientation}</p>
                                            <button onClick={() => setAddPageStep(1)} className="text-xs text-purple-400 hover:text-purple-300 mt-1">← Change layout</button>
                                        </div>
                                    </div>
                                )}

                                {/* Action Choice */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    {/* Blank Page Option */}
                                    <button onClick={() => addBlankPage()}
                                        className="border-2 border-gray-700 hover:border-green-400 rounded-xl p-6 text-left transition-all group">
                                        <div className="w-12 h-12 rounded-xl bg-gray-800 border-2 border-gray-600 group-hover:border-green-400 flex items-center justify-center mb-3 transition-colors">
                                            <FileText className="w-5 h-5 text-gray-400 group-hover:text-green-400" />
                                        </div>
                                        <h4 className="text-white font-semibold mb-1">Add Blank Page</h4>
                                        <p className="text-xs text-gray-500">Empty panels ready for drawing or image upload</p>
                                    </button>

                                    {/* AI Generate Option */}
                                    <div className="border-2 border-purple-500/30 rounded-xl p-6 bg-purple-500/5">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-3">
                                            <Zap className="w-5 h-5 text-white" />
                                        </div>
                                        <h4 className="text-white font-semibold mb-3">Generate with AI</h4>

                                        {/* AI Model Selection */}
                                        <div className="mb-3">
                                            <label className="text-xs text-gray-400 mb-2 block font-semibold">AI MODEL</label>
                                            <div className="grid grid-cols-2 gap-2">
                                                <button onClick={() => setAddPageModel('caravaggio')}
                                                    className={`relative px-3 py-2.5 rounded-lg text-left transition-all text-xs ${addPageModel === 'caravaggio'
                                                        ? 'bg-gradient-to-r from-purple-600/30 to-indigo-600/30 border-2 border-purple-500 text-white'
                                                        : 'bg-gray-800 border-2 border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                                                    {addPageModel === 'caravaggio' && <span className="absolute top-1 right-1 text-green-400">✓</span>}
                                                    <div className="font-bold text-sm">CARAVAGGIO</div>
                                                    <div className="text-[10px] text-orange-400">Custom Model · Fast</div>
                                                </button>
                                                <button onClick={() => setAddPageModel('gemini')}
                                                    className={`relative px-3 py-2.5 rounded-lg text-left transition-all text-xs ${addPageModel === 'gemini'
                                                        ? 'bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border-2 border-blue-500 text-white'
                                                        : 'bg-gray-800 border-2 border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                                                    {addPageModel === 'gemini' && <span className="absolute top-1 right-1 text-green-400">✓</span>}
                                                    <div className="font-bold text-sm">Gemini</div>
                                                    <div className="text-[10px] text-blue-400">Google AI</div>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Story Input */}
                                        <div className="mb-3">
                                            <label className="text-xs text-gray-400 mb-1 block">Your Story / Scene Description *</label>
                                            <textarea value={addPageStory} onChange={e => setAddPageStory(e.target.value)}
                                                placeholder="Describe what happens on this page..."
                                                rows={3}
                                                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50 outline-none" />
                                        </div>

                                        {/* Art Style */}
                                        <div className="mb-3">
                                            <label className="text-xs text-gray-400 mb-1 block">Art Style</label>
                                            <input value={addPageStyle} onChange={e => setAddPageStyle(e.target.value)}
                                                placeholder="e.g., manga, watercolor, cinematic comic..."
                                                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-xs placeholder:text-gray-500 outline-none" />
                                        </div>

                                        {/* ── CHARACTER STUDIO ── */}
                                        <div className="mb-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="text-xs text-gray-400">Characters</label>
                                                <div className="flex items-center gap-2">
                                                    {/* Face Lock Toggle (Caravaggio only) */}
                                                    {addPageModel === 'caravaggio' && characters.length > 0 && (
                                                        <button
                                                            onClick={() => setFaceLockEnabled(!faceLockEnabled)}
                                                            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${
                                                                faceLockEnabled
                                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                                                    : 'bg-gray-700/50 text-gray-500 border border-gray-600'
                                                            }`}
                                                            title="Face Lock: Uses IP-Adapter to maintain character identity across panels"
                                                        >
                                                            {faceLockEnabled ? '🔒' : '🔓'} Face Lock
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Character Cards Gallery */}
                                            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                                                {characters.map((char, idx) => (
                                                    <div key={char.id} className="flex-shrink-0 relative group">
                                                        <div className="w-16 h-20 rounded-lg border-2 border-gray-600 bg-gray-800 overflow-hidden cursor-pointer hover:border-purple-500 transition-all"
                                                            onClick={() => {
                                                                // Remove character on click
                                                                setCharacters(prev => prev.filter(c => c.id !== char.id));
                                                            }}
                                                        >
                                                            {(char.imageBase64 || char.imageUrl) ? (
                                                                <img src={char.imageBase64 || char.imageUrl} alt={char.name}
                                                                    className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-2xl">🧑</div>
                                                            )}
                                                            {/* Remove overlay */}
                                                            <div className="absolute inset-0 bg-red-600/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                                                                <span className="text-white text-xs font-bold">✕</span>
                                                            </div>
                                                        </div>
                                                        <p className="text-[9px] text-gray-400 text-center mt-0.5 truncate w-16">{char.name}</p>
                                                    </div>
                                                ))}

                                                {/* Add Character Button */}
                                                <button
                                                    onClick={() => {
                                                        setEditCharName('');
                                                        setEditCharDesc('');
                                                        setEditCharImage(null);
                                                        setShowCharacterModal(true);
                                                    }}
                                                    className="flex-shrink-0 w-16 h-20 rounded-lg border-2 border-dashed border-gray-600 bg-gray-800/50 hover:border-purple-500 hover:bg-purple-900/20 transition-all flex flex-col items-center justify-center cursor-pointer"
                                                >
                                                    <span className="text-xl text-gray-500">+</span>
                                                    <span className="text-[9px] text-gray-500">Add</span>
                                                </button>
                                            </div>

                                            {/* Fallback: simple text input if no characters added yet */}
                                            {characters.length === 0 && (
                                                <input value={addPageChars} onChange={e => setAddPageChars(e.target.value)}
                                                    placeholder="Or type: Hero: tall, dark hair, red cape..."
                                                    className="w-full mt-2 bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-xs placeholder:text-gray-500 outline-none" />
                                            )}
                                        </div>

                                        {/* Generation Method */}
                                        <div className="flex gap-2 mb-3">
                                            <button onClick={() => setAddPageGenMethod('image')}
                                                className={`flex-1 text-xs py-2 rounded-lg font-medium transition-all ${addPageGenMethod === 'image'
                                                    ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                                                🖼️ Image per Panel
                                            </button>
                                            <button onClick={() => setAddPageGenMethod('video')}
                                                className={`flex-1 text-xs py-2 rounded-lg font-medium transition-all ${addPageGenMethod === 'video'
                                                    ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                                                🎬 Video → Panels
                                            </button>
                                        </div>

                                        <Button onClick={() => { if (isLanding && !requireAuth('generate with AI')) return; generateForPage(); }}
                                            disabled={!addPageStory.trim()}
                                            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white">
                                            <Zap className="w-4 h-4 mr-2" /> Generate & Add Page
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* First Page Choice Dialog (Blank or Generate) */}
            {showFirstPageChoice && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 mb-4">
                                <span className="text-2xl">✨</span>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">How would you like to start?</h3>
                            <p className="text-sm text-gray-400">Your story is ready. Choose how to set up your first page.</p>
                        </div>

                        {project?.story && (
                            <div className="bg-gray-800/50 rounded-lg p-3 mb-6 border border-gray-700">
                                <p className="text-xs text-gray-500 mb-1 font-semibold">YOUR STORY</p>
                                <p className="text-sm text-gray-300 line-clamp-3">{project.story}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            {/* Blank Option */}
                            <button
                                onClick={() => {
                                    setShowFirstPageChoice(false);
                                    toast.info('Starting with blank panels -- you can draw or upload images!');
                                }}
                                className="border-2 border-gray-700 hover:border-green-400 rounded-xl p-5 text-left transition-all group"
                            >
                                <div className="w-10 h-10 rounded-lg bg-gray-800 border-2 border-gray-600 group-hover:border-green-400 flex items-center justify-center mb-3 transition-colors">
                                    <FileText className="w-5 h-5 text-gray-400 group-hover:text-green-400" />
                                </div>
                                <h4 className="text-white font-semibold text-sm mb-1">Start Blank</h4>
                                <p className="text-xs text-gray-500">Empty panels for drawing or uploading</p>
                            </button>

                            {/* Generate Option */}
                            <button
                                onClick={() => {
                                    setShowFirstPageChoice(false);
                                    if (autoGenFnRef.current) {
                                        autoGenFnRef.current();
                                    }
                                }}
                                className="border-2 border-purple-500/50 hover:border-purple-400 rounded-xl p-5 text-left transition-all group bg-purple-500/5"
                            >
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-3">
                                    <Zap className="w-5 h-5 text-white" />
                                </div>
                                <h4 className="text-white font-semibold text-sm mb-1">Generate with AI</h4>
                                <p className="text-xs text-gray-400">AI fills panels from your story</p>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Page Generation Progress Overlay */}
            {isPageGenerating && (
                <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-gray-900 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl shadow-purple-500/10">
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 mb-4 animate-pulse">
                                <span className="text-2xl">🎨</span>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-1">Generating Page</h3>
                            <p className="text-sm text-gray-400">{pageGenProgress.step}</p>
                        </div>
                        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                            {pageGenProgress.panelStatuses.map((ps, idx) => (
                                <PanelProgressRow
                                    key={ps.id}
                                    label={`Panel ${idx + 1}`}
                                    status={ps.status}
                                    index={idx}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Single Panel Regeneration Prompt-Tuner Modal */}
            {showRegenModal && regenPanel && (
                <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !isRegeneratingPanel && setShowRegenModal(false)}>
                    <div className="bg-gray-900 border border-purple-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl shadow-purple-500/10 flex flex-col gap-4 text-left animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-gray-800 pb-3">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Palette className="w-5 h-5 text-purple-400 animate-pulse" /> Tweak & Regenerate Panel {regenPanel.id.toUpperCase()}
                            </h3>
                            {!isRegeneratingPanel && (
                                <button onClick={() => setShowRegenModal(false)} className="text-gray-400 hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>

                        {!isRegeneratingPanel ? (
                            <div className="flex flex-col gap-4">
                                {/* Current panel thumbnail */}
                                {(() => {
                                    const currentPage = pages[currentPageIndex];
                                    const currentImg = currentPage?.panelAssets?.[regenPanel.id];
                                    if (!currentImg) return null;
                                    return (
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Current Panel</label>
                                            <div className="relative rounded-xl overflow-hidden border border-gray-800 bg-gray-950">
                                                <img src={currentImg} alt={`Panel ${regenPanel.id}`}
                                                    className="w-full h-32 object-cover opacity-80" />
                                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent flex items-end p-2">
                                                    <span className="text-[10px] text-gray-400 italic">Tweak the prompt below to regenerate this panel</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                <p className="text-sm text-gray-400">
                                    {regenPrompt ? (
                                        <>The previous prompt is pre-filled below — edit it and regenerate. Style (<strong>{addPageStyle || 'Standard'}</strong>) stays consistent.</>
                                    ) : (
                                        <>Enter a descriptive prompt for this panel. Style (<strong>{addPageStyle || 'Standard'}</strong>) and characters (<strong>{addPageChars || 'Standard'}</strong>) will remain consistent.</>
                                    )}
                                </p>

                                <div>
                                    <label className="text-xs font-bold text-purple-400/80 uppercase tracking-wider block mb-1.5">Panel Description / Prompt *</label>
                                    <textarea
                                        value={regenPrompt}
                                        onChange={e => setRegenPrompt(e.target.value)}
                                        placeholder="e.g. A wide angle shot of the superhero standing tall under the storm, hands glowing with golden electricity..."
                                        rows={4}
                                        className="w-full bg-gray-950 border border-gray-800 focus:border-purple-500 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 placeholder-gray-600 transition-all resize-none"
                                    />
                                </div>

                                {/* Character Reference for this panel */}
                                {regenModel === 'caravaggio' && (
                                    <div>
                                        <label className="text-xs font-bold text-purple-400/80 uppercase tracking-wider block mb-1.5">
                                            Character Reference {regenRefImage ? '🔒' : '(optional)'}
                                        </label>
                                        {regenRefImage ? (
                                            <div className="flex items-start gap-3">
                                                <img src={regenRefImage} alt="Character ref"
                                                    className="w-16 h-16 object-cover rounded-lg border border-gray-700" />
                                                <div className="flex flex-col gap-1.5 flex-1">
                                                    <label className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-[11px] font-medium cursor-pointer transition-colors border border-gray-700">
                                                        📤 Change Image
                                                        <input type="file" accept="image/*" className="hidden" onChange={e => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                const reader = new FileReader();
                                                                reader.onload = ev => setRegenRefImage(ev.target.result);
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }} />
                                                    </label>
                                                    <button onClick={() => setRegenRefImage(null)}
                                                        className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-red-900/50 text-gray-500 hover:text-red-400 text-[11px] font-medium transition-colors border border-gray-700">
                                                        ✕ Remove
                                                    </button>
                                                    <label className="flex items-center gap-2 mt-0.5 cursor-pointer">
                                                        <input type="checkbox" checked={regenFaceLock}
                                                            onChange={e => setRegenFaceLock(e.target.checked)}
                                                            className="rounded bg-gray-800 border-gray-600 text-purple-500 focus:ring-purple-500 w-3.5 h-3.5" />
                                                        <span className="text-[11px] text-gray-400">🔒 Face Lock</span>
                                                    </label>
                                                    {regenFaceLock && (
                                                        <div className="mt-1.5">
                                                            <div className="flex items-center justify-between mb-0.5">
                                                                <span className="text-[10px] text-gray-500">Costume Lock</span>
                                                                <span className="text-[10px] text-purple-400 font-mono">{Math.round(costumeLockStrength * 100)}%</span>
                                                            </div>
                                                            <input type="range" min="50" max="100" value={Math.round(costumeLockStrength * 100)}
                                                                onChange={e => setCostumeLockStrength(parseInt(e.target.value) / 100)}
                                                                className="w-full h-1.5 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                                                            <p className="text-[9px] text-gray-600 mt-0.5">Higher = stricter costume match</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-1.5">
                                                <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-gray-700 bg-gray-950 hover:border-purple-500 hover:bg-purple-900/10 transition-all cursor-pointer text-xs text-gray-500">
                                                    📤 Upload character reference (optional)
                                                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onload = ev => setRegenRefImage(ev.target.result);
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }} />
                                                </label>
                                                <p className="text-[9px] text-gray-600 text-center">💡 Tip: Crop to a single front-facing view for best results</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Model Engine</label>
                                        <div className="flex bg-gray-950 p-1 rounded-xl border border-gray-800">
                                            <button
                                                type="button"
                                                onClick={() => setRegenModel('caravaggio')}
                                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${regenModel === 'caravaggio' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md' : 'text-gray-400 hover:text-white'}`}
                                            >
                                                Caravaggio
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setRegenModel('gemini')}
                                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${regenModel === 'gemini' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md' : 'text-gray-400 hover:text-white'}`}
                                            >
                                                Gemini
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-end">
                                        <button
                                            type="button"
                                            onClick={handleSinglePanelRegenerate}
                                            className="w-full h-11 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-purple-500/10 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                        >
                                            <Zap className="w-4 h-4 text-amber-300 animate-pulse" /> Start Regenerating
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="py-8 flex flex-col items-center justify-center text-center gap-6">
                                <div className="relative flex items-center justify-center">
                                    <div className="w-24 h-24 rounded-full border-4 border-gray-800 border-t-purple-500 animate-spin absolute" />
                                    <span className="text-2xl animate-bounce">🎨</span>
                                </div>
                                <div className="flex flex-col gap-2 max-w-sm">
                                    <h4 className="text-lg font-bold text-white">Re-imagining Panel {regenPanel.id.toUpperCase()}...</h4>
                                    <p className="text-sm text-gray-400">Tuning parameters and generating image via {regenModel === 'caravaggio' ? 'Caravaggio' : 'Gemini'} ({Math.round(regenProgress)}%)</p>
                                </div>
                                
                                {/* Progress bar container */}
                                <div className="w-full max-w-xs h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-300"
                                        style={{ width: `${regenProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/*  */}
            {/* PUBLISH COMIC MODAL */}
            {/*  */}
            {showPublishModal && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowPublishModal(false)}>
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-green-400" /> Publish Comic
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">{pages.length} page{pages.length > 1 ? 's' : ''} will be published as slides in an episode.</p>

                        {/* Mode Tabs */}
                        <div className="flex gap-2 mb-4">
                            <button onClick={() => setPublishMode('new')}
                                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${publishMode === 'new' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                    }`}>New Comic</button>
                            <button onClick={() => setPublishMode('existing')}
                                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${publishMode === 'existing' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                    }`}>Add to Existing</button>
                        </div>

                        {publishMode === 'new' ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Comic Title *</label>
                                    <Input value={publishData.title} onChange={e => setPublishData(p => ({ ...p, title: e.target.value }))}
                                        placeholder="My Awesome Comic" className="bg-gray-800 border-gray-600 text-white" />
                                </div>
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Description</label>
                                    <textarea value={publishData.description} onChange={e => setPublishData(p => ({ ...p, description: e.target.value }))}
                                        placeholder="What's your comic about?" rows={2}
                                        className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-500" />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Select Comic</label>
                                    {myComics.length === 0 ? (
                                        <p className="text-gray-500 text-sm py-4 text-center">No comics found. Create a new one instead!</p>
                                    ) : (
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {myComics.map(c => (
                                                <button key={c.id} onClick={() => { setSelectedComicId(c.id); setSelectedEpisodeId('new'); setEpisodeTitle(`Episode ${(c.episodes_count || 0) + 1}`); }}
                                                    className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm ${selectedComicId === c.id
                                                        ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                                        : 'border-gray-700 text-gray-300 hover:border-gray-500'
                                                        }`}>
                                                    <span className="font-medium">{c.title}</span>
                                                    <span className="text-gray-500 ml-2 text-xs">({c.episodes_count} episode{c.episodes_count !== 1 ? 's' : ''})</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Episode Selector — only show when a comic is selected */}
                                {selectedComicId && (() => {
                                    const selectedComic = myComics.find(c => c.id === selectedComicId);
                                    const episodes = selectedComic?.episodes || [];
                                    return (
                                        <div>
                                            <label className="text-sm text-gray-400 mb-1 block">Add pages to:</label>
                                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                                {episodes.map((ep, idx) => (
                                                    <button key={ep.id} onClick={() => setSelectedEpisodeId(ep.id)}
                                                        className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm flex items-center gap-2 ${selectedEpisodeId === ep.id
                                                            ? 'border-green-500 bg-green-500/10 text-green-300'
                                                            : 'border-gray-700 text-gray-300 hover:border-gray-500'}`}>
                                                        <span className={`inline-block w-2 h-2 rounded-full ${selectedEpisodeId === ep.id ? 'bg-green-400' : 'bg-gray-600'}`} />
                                                        <span>{ep.title || `Episode ${idx + 1}`}</span>
                                                        <span className="text-xs text-gray-500 ml-auto">append</span>
                                                    </button>
                                                ))}
                                                {/* + New Episode option */}
                                                <button onClick={() => { setSelectedEpisodeId('new'); setEpisodeTitle(`Episode ${episodes.length + 1}`); }}
                                                    className={`w-full text-left px-3 py-2 rounded-lg border-2 border-dashed transition-all text-sm flex items-center gap-2 ${selectedEpisodeId === 'new'
                                                        ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                                        : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                                                    <span className="text-lg leading-none">+</span>
                                                    <span>New Episode</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Episode Title — only for new episodes */}
                                {selectedEpisodeId === 'new' && (
                                    <div>
                                        <label className="text-sm text-gray-400 mb-1 block">Episode Title</label>
                                        <Input value={episodeTitle} onChange={e => setEpisodeTitle(e.target.value)}
                                            placeholder="Episode 2" className="bg-gray-800 border-gray-600 text-white" />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex gap-3 mt-6">
                            <Button onClick={() => setShowPublishModal(false)} variant="outline" className="flex-1 border-gray-600 text-gray-300">
                                Cancel
                            </Button>
                            <Button onClick={handlePublish} disabled={isPublishing}
                                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                                {isPublishing ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing...</>
                                ) : (
                                    <><Send className="w-4 h-4 mr-2" /> Publish</>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CHARACTER CREATION MODAL ── */}
            {showCharacterModal && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowCharacterModal(false)}>
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-lg">🎨</div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Create Character</h3>
                                <p className="text-xs text-gray-400">Add a character for consistent panel generation</p>
                            </div>
                            <button onClick={() => setShowCharacterModal(false)} className="ml-auto text-gray-400 hover:text-white text-xl">&times;</button>
                        </div>

                        {/* Character Name */}
                        <div className="mb-3">
                            <label className="text-xs text-gray-400 mb-1 block">Name *</label>
                            <input value={editCharName} onChange={e => setEditCharName(e.target.value)}
                                placeholder="e.g., Kael, Luna, Shadow..."
                                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-500 outline-none focus:border-purple-500" />
                        </div>

                        {/* Character Description */}
                        <div className="mb-3">
                            <label className="text-xs text-gray-400 mb-1 block">Description *</label>
                            <textarea value={editCharDesc} onChange={e => setEditCharDesc(e.target.value)}
                                placeholder="Tall muscular male, 30s, short dark brown hair swept back, tanned olive skin, piercing green eyes, scar on left cheek, dark grey battle armor with gold accents, torn red cape"
                                rows={4}
                                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-500 outline-none focus:border-purple-500" />
                            <p className="text-[10px] text-gray-500 mt-1">💡 Be specific: build, hair, skin tone, eyes, outfit, distinguishing features</p>
                        </div>

                        {/* Character Reference Image */}
                        <div className="mb-4">
                            <label className="text-xs text-gray-400 mb-2 block">Character Reference Image</label>
                            
                            {editCharImage ? (
                                <div className="flex flex-col gap-2">
                                    <div className="relative">
                                        <img src={editCharImage} alt="Character reference"
                                            className="w-full h-40 object-contain rounded-lg border border-gray-600 bg-gray-800" />
                                        <button onClick={() => setEditCharImage(null)}
                                            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">✕</button>
                                    </div>
                                    {/* Auto-Describe Button */}
                                    <button
                                        onClick={async () => {
                                            if (!editCharImage) return;
                                            setIsDescribingChar(true);
                                            try {
                                                const res = await axios.post(`${API}/ai/describe-character`, {
                                                    image: editCharImage
                                                }, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                    timeout: 420000 // 7 min for cold-start JoyCaption
                                                });
                                                if (res.data.success && res.data.description) {
                                                    setEditCharDesc(res.data.description);
                                                    toast.success('✨ Character auto-described!');
                                                } else {
                                                    toast.error('Failed to describe character');
                                                }
                                            } catch (err) {
                                                toast.error(`Auto-describe failed: ${err.response?.data?.detail || err.message}`);
                                            } finally {
                                                setIsDescribingChar(false);
                                            }
                                        }}
                                        disabled={isDescribingChar}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isDescribingChar ? (
                                            <><span className="animate-spin">⏳</span> Analyzing image...</>
                                        ) : (
                                            <><span>🔍</span> Auto-Describe from Image <span className="text-[10px] text-cyan-200">(free)</span></>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    {/* Upload Button */}
                                    <label className="flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed border-gray-600 bg-gray-800/50 hover:border-purple-500 hover:bg-purple-900/10 transition-all cursor-pointer text-sm text-gray-400">
                                        <span>📤</span> Upload Image
                                        <input type="file" accept="image/*" className="hidden" onChange={e => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onload = ev => setEditCharImage(ev.target.result);
                                                reader.readAsDataURL(file);
                                            }
                                        }} />
                                    </label>
                                    
                                    {/* Generate with AI Button */}
                                    <button
                                        onClick={async () => {
                                            if (!editCharDesc.trim()) {
                                                toast.error('Enter a character description first');
                                                return;
                                            }
                                            setIsGeneratingCharacter(true);
                                            try {
                                                const res = await axios.post(`${API}/ai/generate-character-sheet`, {
                                                    name: editCharName || 'Character',
                                                    description: editCharDesc,
                                                    style: addPageStyle || 'comic',
                                                    model_engine: addPageModel || 'caravaggio'
                                                }, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                    timeout: 180000
                                                });
                                                if (res.data.success && res.data.image_url) {
                                                    setEditCharImage(res.data.image_url);
                                                    toast.success(`Character sheet generated! (${res.data.credits_used} credit used)`);
                                                    fetchCredits();
                                                } else {
                                                    toast.error('Failed to generate character sheet');
                                                }
                                            } catch (err) {
                                                toast.error(`Generation failed: ${err.response?.data?.detail || err.message}`);
                                            } finally {
                                                setIsGeneratingCharacter(false);
                                            }
                                        }}
                                        disabled={isGeneratingCharacter || !editCharDesc.trim()}
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed border-gray-600 bg-gray-800/50 hover:border-orange-500 hover:bg-orange-900/10 transition-all text-sm text-gray-400 disabled:opacity-40"
                                    >
                                        {isGeneratingCharacter ? (
                                            <><span className="animate-spin">⏳</span> Generating...</>
                                        ) : (
                                            <><span>⚡</span> Generate with AI<span className="text-[10px] text-gray-500">(1 credit)</span></>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Save Button */}
                        <button
                            onClick={() => {
                                if (!editCharName.trim()) { toast.error('Enter a character name'); return; }
                                if (!editCharDesc.trim()) { toast.error('Enter a character description'); return; }
                                const newChar = {
                                    id: `char_${Date.now()}`,
                                    name: editCharName.trim(),
                                    description: editCharDesc.trim(),
                                    imageUrl: (editCharImage && !editCharImage.startsWith('data:')) ? editCharImage : null,
                                    imageBase64: (editCharImage && editCharImage.startsWith('data:')) ? editCharImage : null,
                                };
                                setCharacters(prev => [...prev, newChar]);
                                setShowCharacterModal(false);
                                toast.success(`Character "${newChar.name}" added!`);
                            }}
                            disabled={!editCharName.trim() || !editCharDesc.trim()}
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-40"
                        >
                            💾 Save Character
                        </button>
                    </div>
                </div>
            )}

            {/* Focus Mode Editor Overlay */}
            {activePanel && (
                <div className="fixed inset-0 z-50 bg-black flex items-center justify-center animate-in fade-in duration-200">
                    <ComicaPro
                        mode="panel"
                        panelWidth={activePanel.width}
                        panelHeight={activePanel.height}
                        initialImage={getEditableImage(panelAssets[activePanel.id])}
                        initialLayers={typeof panelAssets[activePanel.id] === 'object' ? panelAssets[activePanel.id].layers : null}
                        initialAnimation={panelAssets[activePanel.id] && typeof panelAssets[activePanel.id] === 'object' ? (panelAssets[activePanel.id].animation || null) : null}
                        panelClipPath={activePanel.clipPath}
                        panelPoints={activePanel.points}
                        onSavePanel={handleEditorSave}
                        onCancel={() => setActivePanel(null)}
                        onProcessingChange={setEditorProcessing}
                    />

                    {/* Close Button Override — hidden during timelapse processing */}
                    {!editorProcessing && (
                        <button
                            onClick={() => setActivePanel(null)}
                            className="absolute top-4 right-4 z-[100] bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg hover:scale-110 transition-transform"
                            title="Close Editor"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    )}
                </div>
            )}

        </div>
    );
};

export default ComicBlueprint;
